import {t, useLanguage} from '../i18n';
import {recommendedMessages,withMessageChoice,messageReadiness} from '../utils/messagePreferences';
import {usePendingEditor} from "../hooks/useEditorNavigation";
// src/sections/NotificationSection.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getFunctions, httpsCallable } from "firebase/functions";
import { db as defaultDb } from "../firebase";
import {
  loopBusinessGetDoc,
  loopBusinessIsPlatformOwner,
  loopBusinessNow,
  loopBusinessSetDoc,
  loopBusinessUpdateDoc,
} from "../utils/businessNativeSafeWrites";
import { callLoopOwnerBusinessAdmin } from "../utils/platformOwner";
import PaymentReceiptSettings from "../components/payments/PaymentReceiptSettings";

const DEFAULT_BUSINESS_NOTIFICATION_SETTINGS = {
  onlineBookingRequiresApproval: true,

  notifyOwnerOnNewBooking: true,
  notifyAdminsOnNewBooking: true,
  notifyAssignedWorker: true,

  sendCustomerEmailOnRequest: true,
  sendCustomerEmailOnApproval: true,
  sendCustomerEmailOnRejection: true,
  sendCustomerEmailOnCancellation: true,

  customerReminder24h: true,
  customerReminder2h: true,
  workerReminder2h: true,

  whatsappEnabled: true,
  whatsappManualMessagesEnabled: true,
  whatsappReviewRequestEnabled: false,
  whatsappAutoConfirmEnabled: false,
  whatsappAutoCancellationEnabled: false,
  whatsappReminder24hEnabled: false,
  whatsappReminder2hEnabled: false,
  whatsappTemplateRemindersLive: false,

  popupNewBookingInApp: true,
};

function normalizeSettings(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    ...DEFAULT_BUSINESS_NOTIFICATION_SETTINGS,
    ...Object.fromEntries(
      Object.keys(DEFAULT_BUSINESS_NOTIFICATION_SETTINGS).map((key) => [
        key,
        typeof source[key] === "boolean" ? source[key] : DEFAULT_BUSINESS_NOTIFICATION_SETTINGS[key],
      ])
    ),
  };
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      className={`notify-switch ${checked ? "is-on" : ""}`}
      onClick={() => onChange(!checked)}
      aria-label={label}
      aria-pressed={checked}
    >
      <span />
    </button>
  );
}

function SettingRow({ title, copy, checked, onChange }) {
  return (
    <div className="notify-setting-row">
      <div>
        <strong>{title}</strong>
        {copy ? <span>{copy}</span> : null}
      </div>
      <Switch checked={checked} onChange={onChange} label={title} />
    </div>
  );
}

function SettingsGroup({ eyebrow, title, copy, children }) {
  return (
    <section className="notify-card">
      <div className="notify-card-head">
        <span>{eyebrow}</span>
        <h3>{title}</h3>
        {copy ? <p>{copy}</p> : null}
      </div>
      <div className="notify-card-body">{children}</div>
    </section>
  );
}


function formatHealthDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("es-PA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "—";
  }
}

function normalizeWa(value) {
  return String(value || "").trim().toLowerCase();
}

function healthStatusLabel(value) {
  const s = normalizeWa(value || "unknown");
  if (["queued", "sending", "pending"].includes(s)) return t("Enviando");
  if (s === "sent") return t("Enviado");
  if (s === "delivered") return t("Entregado");
  if (s === "read") return t("Leído");
  if (s === "failed" || s === "error") return t("Fallido");
  if (s === "skipped") return t("Omitido");
  return t("Sin estado");
}

function whatsappDeliveryStatusLabel(value) {
  const s = normalizeWa(value);
  if (s === "sent") return t("Aceptado por Meta");
  if (s === "delivered") return t("Entregado al teléfono");
  if (s === "read") return t("Leído por el cliente");
  if (s === "failed") return t("Falló en Meta");
  return t("Sin delivery");
}

function whatsappProviderStatusLabel(value) {
  const s = normalizeWa(value);
  if (s === "sent") return t("Aceptado por Meta");
  if (s === "delivered") return t("Entregado");
  if (s === "read") return t("Leído");
  if (s === "failed") return t("Falló");
  return t("—");
}

function whatsappSkipReasonLabel(value) {
  const raw = String(value || "").trim();
  const s = normalizeWa(raw);
  const labels = {
    "monthly-limit-reached": "Límite mensual alcanzado",
    "customer-opted-out": "Cliente no acepta WhatsApp",
    "missing-customer-phone": "Cliente sin teléfono",
    "whatsapp-disabled": "WhatsApp apagado en el negocio",
    "disabled-by-business-setting": "Regla apagada por el negocio",
    "business-setting-disabled": "Regla apagada por el negocio",
    "template-automation-not-live": "Recordatorios omitidos por activación incompleta",
    "already-sent": "Ya se había enviado",
    "send-in-progress": "Envío en progreso",
    "not-confirmed": "Reserva no confirmada",
    "booking-not-found": "Reserva no encontrada",
    "feature-disabled-by-billing:whatsappautomation": "El plan del negocio no incluye esta automatización",
  };
  if (labels[s]) return t(labels[s]);
  if (s.startsWith("disabled-by-business-setting")) return t("Regla apagada por el negocio");
  if (s.startsWith("feature-disabled-by-billing")) return t("Función desactivada por billing");
  return t(raw || "Sin motivo");
}

function whatsappMessageEffectiveStatus(row = {}) {
  const delivery = normalizeWa(row.deliveryStatus || row.providerDeliveryStatus || row.providerStatus || row.lastProviderStatus);
  if (["read", "delivered", "failed"].includes(delivery)) return delivery;
  const status = normalizeWa(row.status || (row.ok === true ? "sent" : row.ok === false ? "failed" : "unknown"));
  return status || "unknown";
}

function whatsappMessageStatusCopy(row = {}) {
  const effective = whatsappMessageEffectiveStatus(row);
  if (effective === "read") return t("El cliente abrió el mensaje en WhatsApp.");
  if (effective === "delivered") return t("Meta confirmó que llegó al teléfono del cliente.");
  if (effective === "sent") return t("Meta aceptó el mensaje. Esperando delivery/read.");
  if (effective === "failed") return t("Meta o Loop reportó un fallo en este envío.");
  if (effective === "skipped") return t(whatsappSkipReasonLabel(row.reason || row.skipReason || row.meta?.reason));
  return t("Mensaje registrado sin estado final todavía.");
}


function whatsappAutomationLabel(value) {
  const raw = String(value || "").trim();
  const s = normalizeWa(raw);
  if (!raw) return t("");
  if (s.includes("booking_confirmed_auto")) return t("Automático · Confirmación");
  if (s.includes("booking_cancelled_auto") || s.includes("booking_canceled_auto")) return t("Automático · Cancelación");
  if (s.includes("booking_reminder_24h_auto")) return t("Automático · Recordatorio 24h");
  if (s.includes("booking_reminder_2h_auto")) return t("Automático · Recordatorio 2h");
  if (s.includes("manual")) return t("Manual");
  if (s.includes("auto")) return t("Automático");
  return t(whatsappSkipReasonLabel(raw));
}

function whatsappTemplateFriendlyName(value) {
  const s = normalizeWa(value);
  if (!s) return t("");
  if (s.includes("booking_confirmed")) return t("Confirmación");
  if (s.includes("booking_cancelled") || s.includes("booking_canceled")) return t("Cancelación");
  if (s.includes("booking_reminder_24h")) return t("Recordatorio 24h");
  if (s.includes("booking_reminder_2h")) return t("Recordatorio 2h");
  return t(value || "");
}

function whatsappMessageTechnicalSummary(row = {}) {
  const bits = [
    row.templateName ? `Template: ${row.templateName}` : "",
    row.type ? `Tipo raw: ${row.type}` : "",
    row.reason || row.skipReason ? `Motivo raw: ${row.reason || row.skipReason}` : "",
    row.bookingId ? `Booking ID: ${row.bookingId}` : "",
    row.metaMessageId ? `Meta wamid: ${row.metaMessageId}` : "",
    row.providerStatus ? `Provider: ${row.providerStatus}` : "",
    row.deliveryStatus ? `Delivery raw: ${row.deliveryStatus}` : "",
    row.providerError ? `Provider error: ${row.providerError}` : "",
    row.error ? `Error: ${row.error}` : "",
  ].filter(Boolean);
  return bits;
}

function whatsappMessageTone(statusOrRow) {
  const s = typeof statusOrRow === "object" ? whatsappMessageEffectiveStatus(statusOrRow) : normalizeWa(statusOrRow);
  if (s === "read") return "read";
  if (s === "delivered") return "delivered";
  if (s === "sent") return "sent";
  if (s === "failed" || s === "error") return "failed";
  if (s === "skipped") return "skipped";
  return "unknown";
}

function whatsappWebhookDiagnosticTone(diag, counts) {
  if (!diag || diag.hasStatusWebhookEvent !== true) {
    return Number(counts?.sent || 0) > 0 ? "warn" : "neutral";
  }
  if (diag.lastMatched === false) return "warn";
  return "ok";
}

function whatsappWebhookDiagnosticTitle(diag) {
  if (!diag || diag.hasStatusWebhookEvent !== true) return t("Esperando primer status");
  if (diag.lastMatched === false) return t("Status recibido sin match");
  const label = whatsappProviderStatusLabel(diag.lastProviderStatus);
  return t(label && label !== "—" ? t("Webhook activo · {label}",{label}) : "Webhook activo");
}

function whatsappWebhookDiagnosticCopy(diag, counts) {
  if (!diag || diag.hasStatusWebhookEvent !== true) {
    return t(Number(counts?.sent || 0) > 0
      ? "Loop ya envió mensajes, pero aún no aparece un status nuevo. Haz una prueba con un WhatsApp recién enviado y abierto por el cliente."
      : "Aún no hay envíos recientes para validar estados delivered/read.");
  }
  if (diag.lastMatched === false) {
    return t("Meta sí envió un status, pero Loop no encontró el mensaje por wamid. Revisa el índice whatsappMessageIndex.");
  }
  return t("Loop está recibiendo estados de Meta y actualizando los mensajes del negocio correctamente.");
}

function whatsappTestBodyParams(templateName, businessDoc = {}) {
  const template = normalizeWa(templateName);
  const businessName = businessDoc?.name || businessDoc?.businessName || businessDoc?.displayName || "Loop Reservas";
  const customer = "Cliente de prueba";
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString("es-PA", { day: "numeric", month: "long", year: "numeric" });
  const time = "10:00";
  const url = "https://www.loopreservas.com/my-bookings?source=whatsapp_test";
  if (template.includes("reminder_2h")) return [customer, businessName, time, url];
  return [customer, businessName, date, time, url];
}

function WhatsAppHealthCard({ health, loading, error, onRefresh }) {
  const counts = health?.counts || { total: 0, sent: 0, failed: 0, skipped: 0 };
  const checks = Array.isArray(health?.checks) ? health.checks : [];
  const recommendations = Array.isArray(health?.recommendations) ? health.recommendations : [];
  const recentLogs = Array.isArray(health?.recentLogs) ? health.recentLogs : [];
  const allCriticalOk = checks.filter((x) => ["whatsappEnabled", "accessToken", "phoneNumberId"].includes(x.key)).every((x) => x.ok);

  return (
    <section className="notify-whatsapp-health">
      <div className="notify-health-head">
        <div>
          <span>Centro de salud WhatsApp</span>
          <h3>{loading ? "Revisando canal..." : allCriticalOk ? "Canal listo para operar" : "Canal requiere atención"}</h3>
          <p>Visibilidad rápida de configuración, plantillas y últimos envíos del negocio.</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading}>
          {loading ? "Revisando..." : t("Actualizar estado")}
        </button>
      </div>

      {error ? <div className="notify-health-error">{error}</div> : null}

      <div className="notify-health-metrics">
        <div><strong>{counts.sent || 0}</strong><span>Enviados recientes</span></div>
        <div><strong>{counts.delivered || 0}</strong><span>Entregados</span></div>
        <div><strong>{counts.read || 0}</strong><span>Leídos</span></div>
        <div><strong>{health?.config?.phoneNumberIdLast4 ? `••••${health.config.phoneNumberIdLast4}` : "—"}</strong><span>Phone ID</span></div>
      </div>

      <div className="notify-health-checks">
        {checks.map((check) => (
          <div key={check.key} className={`notify-health-check ${check.ok ? "ok" : "warn"}`}>
            <b>{check.ok ? "✓" : "!"}</b>
            <span>{t(check.label)}</span>
          </div>
        ))}
      </div>

      <div className="notify-health-two-col">
        <div className="notify-health-box">
          <strong>Plantillas configuradas</strong>
          <p>Confirmación: {health?.templates?.booking_confirmed || "booking_confirmed"}</p>
          <p>Cancelación: {health?.templates?.booking_cancelled || "booking_cancelled"}</p>
          <p>24h: {health?.templates?.booking_reminder_24h || "booking_reminder_24h"}</p>
          <p>2h: {health?.templates?.booking_reminder_2h || "booking_reminder_2h"}</p>
        </div>
        <div className="notify-health-box">
          <strong>{t("Recomendación")}</strong>
          {recommendations.slice(0, 2).map((item, idx) => <p key={idx}>{item}</p>)}
        </div>
      </div>

      {recentLogs.length ? (
        <div className="notify-health-recent">
          <strong>Últimos WhatsApp</strong>
          {recentLogs.slice(0, 4).map((row) => (
            <div key={`${row.bookingId}-${row.id}`}>
              <span>{healthStatusLabel(whatsappMessageEffectiveStatus(row))} · {whatsappMessageTypeLabel(row.templateName || row.type || "WhatsApp")}</span>
              <em>{formatHealthDate(row.providerStatusUpdatedAt || row.createdAt)}</em>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}


function whatsappMessageTypeLabel(value) {
  const s = String(value || "").toLowerCase();
  if (s.includes("booking_confirmed") || s.includes("confirmed")) return t("Confirmación");
  if (s.includes("booking_cancelled") || s.includes("cancelled") || s.includes("canceled")) return t("Cancelación");
  if (s.includes("reminder_24h") || s.includes("24h")) return t("Recordatorio 24h");
  if (s.includes("reminder_2h") || s.includes("2h")) return t("Recordatorio 2h");
  if (s.includes("manual")) return t("Manual");
  return t(value || "WhatsApp");
}

function WhatsAppTestCard({ businessId, businessDoc = {}, health, onSent }) {
  const templates = health?.templates || {};
  const defaultTemplate = templates.booking_confirmed || "booking_confirmed";
  const defaultPhone = String(
    businessDoc.whatsappTestPhone ||
    businessDoc.testWhatsappPhone ||
    businessDoc.ownerPhone ||
    businessDoc.phone ||
    businessDoc.phoneNumber ||
    businessDoc.whatsapp ||
    ""
  ).trim();
  const [to, setTo] = useState(defaultPhone);
  const [templateName, setTemplateName] = useState(defaultTemplate);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!to && defaultPhone) setTo(defaultPhone);
  }, [defaultPhone, to]);

  useEffect(() => {
    if (templateName === "booking_confirmed" && defaultTemplate !== "booking_confirmed") setTemplateName(defaultTemplate);
  }, [defaultTemplate, templateName]);

  const sendTest = async () => {
    if (!businessId) {
      setStatus("No hay negocio activo para enviar la prueba.");
      return;
    }
    if (!String(to || "").trim()) {
      setStatus("Agrega un número de WhatsApp para enviar la prueba.");
      return;
    }
    setLoading(true);
    setStatus("Enviando prueba WhatsApp...");
    try {
      const fn = httpsCallable(getFunctions(), "sendWhatsAppTemplateTest");
      const res = await fn({
        businessId,
        to,
        templateName,
        languageCode: health?.config?.language || "es_PA",
        bodyParams: whatsappTestBodyParams(templateName, businessDoc),
      });
      const messageId = res?.data?.messageId || "";
      setStatus(messageId ? "Prueba enviada. Abre el WhatsApp para validar Entregado/Leído." : "Prueba enviada. Revisa el centro de mensajes.");
      if (typeof onSent === "function") onSent();
    } catch (err) {
      console.error("[NotificationSection] WhatsApp test failed", err);
      setStatus(err?.message || err?.details?.message || "No se pudo enviar la prueba WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="notify-whatsapp-test">
      <div className="notify-test-head">
        <div>
          <span>Prueba rápida WhatsApp</span>
          <h3>Valida envío, delivery y leído sin crear una reserva</h3>
          <p>Envía una plantilla aprobada a un número real. Luego abre el mensaje y actualiza el centro de mensajes.</p>
        </div>
        <button type="button" onClick={sendTest} disabled={loading}>{loading ? t("Enviando...") : "Enviar prueba"}</button>
      </div>
      <div className="notify-test-form">
        <label>
          <span>Número destino</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+50761504816" />
        </label>
        <label>
          <span>Plantilla</span>
          <select value={templateName} onChange={(e) => setTemplateName(e.target.value)}>
            <option value={templates.booking_confirmed || "booking_confirmed"}>{t("Confirmación")}</option>
            <option value={templates.booking_cancelled || "booking_cancelled"}>Cancelación</option>
            <option value={templates.booking_reminder_24h || "booking_reminder_24h"}>Recordatorio 24h</option>
            <option value={templates.booking_reminder_2h || "booking_reminder_2h"}>Recordatorio 2h</option>
          </select>
        </label>
      </div>
      {status ? <p className={`notify-test-status ${/no se pudo|error|fall/i.test(status) ? "is-error" : "is-ok"}`}>{t(status)}</p> : null}
    </section>
  );
}

function WhatsAppPublishChecklist({ health, messages }) {
  const checks = Array.isArray(health?.checks) ? health.checks : [];
  const counts = messages?.counts || health?.counts || {};
  const diag = messages?.webhookDiagnostics || health?.webhookDiagnostics || null;
  const checkOk = (key) => checks.find((item) => item.key === key)?.ok === true;
  const items = [
    { key: "channel", label: "Canal activo", ok: checkOk("whatsappEnabled") && checkOk("accessToken") && checkOk("phoneNumberId") },
    { key: "webhookToken", label: "Verify token seguro", ok: health?.config?.hasWebhookVerifyToken === true || checkOk("webhookVerifyToken") },
    { key: "message", label: "Prueba enviada", ok: Number(counts.sent || 0) > 0 },
    { key: "delivery", label: "Delivery recibido", ok: Number(counts.delivered || 0) > 0 || Number(counts.read || 0) > 0 },
    { key: "read", label: "Lectura recibida", ok: Number(counts.read || 0) > 0 },
    { key: "match", label: "Webhook conectado", ok: diag?.lastMatched === true },
    { key: "errors", label: "Sin fallo reciente", ok: !health?.lastError },
  ];
  const completed = items.filter((item) => item.ok).length;
  const ready = completed >= 6;

  return (
    <section className={`notify-whatsapp-publish ${ready ? "ready" : "pending"}`}>
      <div className="notify-publish-head">
        <div>
          <span>Checklist WhatsApp</span>
          <h3>{ready ? "Listo para piloto real" : "Últimas validaciones"}</h3>
          <p>Resumen simple para publicar sin mostrar datos técnicos al cliente.</p>
        </div>
        <strong>{completed}/{items.length}</strong>
      </div>
      <div className="notify-publish-grid">
        {items.map((item) => (
          <div key={item.key} className={item.ok ? "ok" : "pending"}>
            <b>{item.ok ? "✓" : "•"}</b>
            <span>{t(item.label)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhatsAppUsageCard({ usage, loading, error, onRefresh }) {
  const limit = Number(usage?.monthlyLimit ?? 100);
  const unlimited = usage?.unlimited === true || limit < 0;
  const sent = Number(usage?.sentCount || 0);
  const reserved = Number(usage?.reservedCount || 0);
  const remaining = unlimited ? null : Math.max(0, Number(usage?.remaining ?? (limit - sent - reserved)));
  const percent = unlimited ? 0 : Math.min(100, Math.max(0, Number(usage?.usagePercent || 0)));
  const isWarning = !unlimited && percent >= 80 && percent < 100;
  const isBlocked = !unlimited && remaining <= 0;

  return (
    <section className={`notify-whatsapp-usage ${isBlocked ? "blocked" : isWarning ? "warning" : "ok"}`}>
      <div className="notify-usage-head">
        <div>
          <span>Consumo mensual WhatsApp</span>
          <h3>{loading ? "Leyendo consumo..." : isBlocked ? "Límite mensual alcanzado" : isWarning ? "Cerca del límite mensual" : "Consumo bajo control"}</h3>
          <p>Control por negocio para evitar envíos ilimitados desde el número central de Loop.</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading}>{loading ? "Actualizando..." : t("Actualizar consumo")}</button>
      </div>
      {error ? <div className="notify-health-error">{error}</div> : null}
      <div className="notify-usage-grid">
        <div><strong>{sent}</strong><span>Enviados este mes</span></div>
        <div><strong>{unlimited ? "∞" : limit}</strong><span>Límite mensual</span></div>
        <div><strong>{unlimited ? "∞" : remaining}</strong><span>Disponibles</span></div>
        <div><strong>{usage?.monthKey || "—"}</strong><span>{t("Mes")}</span></div>
      </div>
      {!unlimited ? (
        <div className="notify-usage-progress" aria-label={`Consumo WhatsApp ${percent}%`}>
          <span style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      <div className="notify-usage-breakdown">
        <span>Confirmaciones: {usage?.confirmationCount || 0}</span>
        <span>Cancelaciones: {usage?.cancellationCount || 0}</span>
        <span>24h: {usage?.reminder24hCount || 0}</span>
        <span>2h: {usage?.reminder2hCount || 0}</span>
        <span>Manual: {usage?.manualCount || 0}</span>
      </div>
      {isBlocked ? <p className="notify-usage-note">Los próximos WhatsApp se registrarán como saltados por límite mensual hasta que subas el límite o cambie el mes.</p> : null}
      {isWarning ? <p className="notify-usage-note">Este negocio ya usó más del 80% del límite mensual. Buen momento para revisar plan o consumo.</p> : null}
    </section>
  );
}

function WhatsAppMessagesCenter({ data, loading, error, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedDetails, setExpandedDetails] = useState({});
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const counts = data?.counts || { total: 0, sent: 0, failed: 0, skipped: 0, unknown: 0, delivered: 0, read: 0, providerFailed: 0 };

  const visibleRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return rows.filter((row) => {
      const status = whatsappMessageEffectiveStatus(row);
      const typeText = [row.type, row.templateName, row.reason].filter(Boolean).join(" ").toLowerCase();
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (typeFilter !== "all" && !typeText.includes(typeFilter)) return false;
      if (!q) return true;
      const haystack = [
        row.customerName,
        row.serviceName,
        row.bookingId,
        row.templateName,
        row.type,
        row.to,
        row.toMasked,
        row.error,
        row.reason,
        row.deliveryStatus,
        row.providerStatus,
        row.providerError,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search, statusFilter, typeFilter]);

  return (
    <section className="notify-whatsapp-messages">
      <div className="notify-msg-head">
        <div>
          <span>Centro de mensajes WhatsApp</span>
          <h3>Seguimiento limpio de WhatsApp</h3>
          <p>Ve qué mensajes llegaron y cuáles fueron leídos. Los detalles técnicos quedan guardados, pero no estorban.</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading}>{loading ? "Cargando..." : t("Actualizar")}</button>
      </div>

      {error ? <div className="notify-health-error">{error}</div> : null}

      {(() => {
        const diag = data?.webhookDiagnostics || null;
        const tone = whatsappWebhookDiagnosticTone(diag, counts);
        return (
          <div className={`notify-webhook-diagnostic ${tone}`}>
            <div>
              <span>Estado de conexión</span>
              <strong>{whatsappWebhookDiagnosticTitle(diag)}</strong>
              <p>{whatsappWebhookDiagnosticCopy(diag, counts)}</p>
            </div>
            <div>
              <small>Último evento: {whatsappProviderStatusLabel(diag?.lastProviderStatus)}</small>
              <small>Recibido: {formatHealthDate(diag?.lastStatusReceivedAt || diag?.lastWebhookReceivedAt)}</small>
              <small>{diag?.lastMatched === true ? "Conectado con la reserva" : diag?.lastMatched === false ? "Sin match técnico" : "Pendiente de evento"}</small>
            </div>
          </div>
        );
      })()}

      <div className="notify-msg-summary">
        <div><strong>{counts.total || 0}</strong><span>Total cargado</span></div>
        <div><strong>{counts.sent || 0}</strong><span>Enviados</span></div>
        <div><strong>{counts.delivered || 0}</strong><span>Entregados</span></div>
        <div><strong>{counts.read || 0}</strong><span>Leídos</span></div>
        <div><strong>{counts.failed || 0}</strong><span>Fallidos</span></div>
        <div><strong>{counts.skipped || 0}</strong><span>{t("Saltados")}</span></div>
      </div>

      <div className="notify-msg-toolbar">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente, servicio, teléfono o tipo..." />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">{t("Todos los estados")}</option>
          <option value="sent">Enviados</option>
          <option value="delivered">Entregados</option>
          <option value="read">Leídos</option>
          <option value="failed">Fallidos</option>
          <option value="skipped">{t("Saltados")}</option>
          <option value="unknown">Sin estado</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">Todos los tipos</option>
          <option value="confirmed">{t("Confirmación")}</option>
          <option value="cancelled">Cancelación</option>
          <option value="24h">Recordatorio 24h</option>
          <option value="2h">Recordatorio 2h</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      <div className="notify-msg-list">
        {loading && !rows.length ? <div className="notify-msg-empty">Cargando últimos mensajes...</div> : null}
        {!loading && !visibleRows.length ? <div className="notify-msg-empty">No hay mensajes que coincidan con los filtros.</div> : null}
        {visibleRows.slice(0, 60).map((row) => {
          const effectiveStatus = whatsappMessageEffectiveStatus(row);
          const tone = whatsappMessageTone(row);
          const typeLabel = whatsappMessageTypeLabel(row.templateName || row.type || row.reason);
          const templateLabel = whatsappTemplateFriendlyName(row.templateName || row.type);
          const customerLabel = row.customerName || row.clientName || row.bookingCustomerName || (row.toMasked || row.to ? `Cliente ${row.toMasked || row.to}` : "Cliente");
          const reasonLabel = row.reason || row.skipReason ? whatsappAutomationLabel(row.reason || row.skipReason) : (row.providerError || row.error || "");
          const technicalBits = whatsappMessageTechnicalSummary(row);
          const rowKey = `${row.bookingId || "booking"}-${row.id || row.metaMessageId || row.createdAt || row.reason || "row"}`;
          const isExpanded = expandedDetails[rowKey] === true;
          return (
            <article key={rowKey} className={`notify-msg-row ${tone}`}>
              <div className="notify-msg-main">
                <span className={`notify-msg-status ${tone}`}>{healthStatusLabel(effectiveStatus)}</span>
                <strong>{typeLabel}</strong>
                <p>{customerLabel}{row.serviceName ? ` · ${row.serviceName}` : ""}</p>
                <em>{whatsappMessageStatusCopy(row)}</em>
                {reasonLabel ? <small>{reasonLabel}</small> : null}
              </div>
              <div className="notify-msg-meta">
                <span>{row.toMasked || row.to || "Sin teléfono"}</span>
                {templateLabel ? <span>Tipo: {templateLabel}</span> : null}
                <span>{row.providerStatusUpdatedAt ? "Actualizado" : "Creado"}: {formatHealthDate(row.providerStatusUpdatedAt || row.createdAt)}</span>
                {row.deliveryStatus ? <small className={`notify-delivery ${String(row.deliveryStatus).toLowerCase()}`}>{whatsappDeliveryStatusLabel(row.deliveryStatus)}</small> : null}
                {technicalBits.length ? (
                  <button
                    type="button"
                    className="notify-msg-details-btn"
                    onClick={() => setExpandedDetails((current) => ({ ...current, [rowKey]: !current[rowKey] }))}
                  >
                    {isExpanded ? "Ocultar detalles" : t("Ver detalles")}
                  </button>
                ) : null}
                {isExpanded ? (
                  <div className="notify-msg-tech">
                    {technicalBits.map((bit) => <small key={bit}>{bit}</small>)}
                  </div>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}


function MessageIcon({ name }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.1,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  const paths = {
    message: (
      <>
        <path d="M5 6.5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-5 3v-12a2 2 0 0 1 2-2Z" />
        <path d="M8 10h8" />
        <path d="M8 13.5h5" />
      </>
    ),
    email: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="m4.5 7 7.5 6 7.5-6" />
      </>
    ),
    whatsapp: (
      <>
        <path d="M20.3 11.8a8.25 8.25 0 0 1-12.1 7.25L4.2 20l1.05-3.8A8.25 8.25 0 1 1 20.3 11.8Z" />
        <path d="M8.85 8.85c.2-.45.42-.48.72-.48h.56c.2 0 .42.06.62.52.2.46.72 1.75.78 1.88.07.14.1.3 0 .48-.1.18-.16.28-.3.44-.15.15-.32.34-.46.47-.16.14-.32.3-.13.6.18.3.8 1.3 1.7 2.08 1.17 1.02 2.15 1.34 2.46 1.48.31.14.5.12.68-.08.18-.2.78-.92 1-1.23.21-.32.42-.27.71-.16.3.1 1.86.88 2.18 1.04.31.16.52.24.6.38" />
      </>
    ),
    push: (
      <>
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
        <path d="M10 19a2 2 0 0 0 4 0" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M16 3v4" />
        <path d="M8 3v4" />
        <path d="M3 10h18" />
      </>
    ),
    cancel: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15 9-6 6" />
        <path d="m9 9 6 6" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.2 2.2 2.2 4.9-5" />
      </>
    ),
    send: (
      <>
        <path d="m22 2-7 20-4-9-9-4 20-7Z" />
        <path d="M22 2 11 13" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    alert: (
      <>
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      </>
    ),
    chart: (
      <>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="m7 15 4-4 3 3 5-7" />
      </>
    ),
    shield: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    crown: (
      <>
        <path d="m3 8 4 3 5-7 5 7 4-3-2 10H5L3 8Z" />
        <path d="M5 18h14" />
      </>
    ),
    gear: (
      <>
        <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.05.05a2.05 2.05 0 0 1-2.9 2.9l-.05-.05A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.08V21a2.05 2.05 0 0 1-4.1 0v-.07A1.7 1.7 0 0 0 8.1 19.3a1.7 1.7 0 0 0-1.88.34l-.05.05a2.05 2.05 0 1 1-2.9-2.9l.05-.05A1.7 1.7 0 0 0 3.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.08-.4H2a2.05 2.05 0 0 1 0-4.1h.07A1.7 1.7 0 0 0 3.7 8.1a1.7 1.7 0 0 0-.34-1.88l-.05-.05a2.05 2.05 0 0 1 2.9-2.9l.05.05A1.7 1.7 0 0 0 8 3.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.08V2a2.05 2.05 0 0 1 4.1 0v.07A1.7 1.7 0 0 0 14.9 3.7a1.7 1.7 0 0 0 1.88-.34l.05-.05a2.05 2.05 0 1 1 2.9 2.9l-.05.05A1.7 1.7 0 0 0 20.4 8c.25.38.6.7 1 .9.34.17.7.25 1.08.25H22a2.05 2.05 0 0 1 0 4.1h-.07A1.7 1.7 0 0 0 20.3 14.9Z" />
      </>
    ),
    book: (
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="11" width="16" height="10" rx="2.5" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </>
    ),
  };

  return <span className={`msg-icon msg-icon-${name}`}><svg {...common}>{paths[name] || paths.message}</svg></span>;
}

function MiniChannel({ type, active = true, label }) {
  return (
    <span className={`msg-mini-channel ${type} ${active ? "active" : "muted"}`} title={label || type}>
      <MessageIcon name={type} />
    </span>
  );
}

function MessageToggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      className={`msg-toggle ${checked ? "is-on" : ""}`}
      onClick={() => onChange(!checked)}
      aria-label={label}
      aria-pressed={checked}
    >
      <span />
    </button>
  );
}

function buildSafeDateLabel() {
  try {
    return new Date().toLocaleDateString("es-PA", { day: "numeric", month: "short", year: "numeric" });
  } catch (_) {
    return "Hoy";
  }
}



export default function NotificationSection({ businessId, businessDoc = {}, db = defaultDb, externalSaveRef = null, hideLocalActions = false }) {
  useLanguage();
  const [settings, setSettings] = useState(() => normalizeSettings(businessDoc?.notificationSettings));
  const [dirty,setDirty]=useState(false);
  const [showRecommended,setShowRecommended]=useState(false);

  const [activeTab, setActiveTab] = useState("automations");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  usePendingEditor({pending:dirty,busy:saving,title:"Mensajes"});
  const [status, setStatus] = useState("");
  const [whatsappHealth, setWhatsappHealth] = useState(null);
  const [whatsappHealthLoading, setWhatsappHealthLoading] = useState(false);
  const [whatsappHealthError, setWhatsappHealthError] = useState("");
  const [whatsappMessages, setWhatsappMessages] = useState(null);
  const [whatsappMessagesLoading, setWhatsappMessagesLoading] = useState(false);
  const [whatsappMessagesError, setWhatsappMessagesError] = useState("");
  const [whatsappUsage, setWhatsappUsage] = useState(null);
  const [whatsappUsageLoading, setWhatsappUsageLoading] = useState(false);
  const [whatsappUsageError, setWhatsappUsageError] = useState("");
  const [activityStatusFilter, setActivityStatusFilter] = useState("all");
  const [activitySearch, setActivitySearch] = useState("");
 const [activityLimit,setActivityLimit]=useState(20);
 const currentBusiness=useRef(businessId);currentBusiness.current=businessId;

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!businessId || !db) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        let subdoc = null;
        try {
          subdoc = await loopBusinessGetDoc(db, `businesses/${businessId}/notificationSettings/main`);
        } catch (_) {
          // The root business mirror remains a safe fallback for older businesses.
        }
        if (!mounted) return;
        setSettings(normalizeSettings(subdoc || businessDoc?.notificationSettings || {}));
      } catch (err) {
        console.warn("[NotificationSection] load failed", err);
        if (mounted) setSettings(normalizeSettings(businessDoc?.notificationSettings || {}));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [businessId, db, businessDoc?.notificationSettings]);

  const setValue = (key, value) => {
    setDirty(true);
    setStatus("Cambios sin guardar.");
    setSettings((prev) => withMessageChoice(prev,key,value));
  };

  const setMany = (patch) => {
    setDirty(true);
    setStatus("Cambios sin guardar.");
    setSettings((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(patch || {}).map(([key, value]) => [key, value === true])) }));
  };

  const saveSettings = useCallback(async () => {
    if (!businessId || !db) return { ok: false, message: "No hay negocio activo para guardar." };
    if(loading||saving)return {ok:false,message:"Espera a que termine la carga o el guardado."};
    setSaving(true);
    setStatus("");
    try {
      const normalized = normalizeSettings(settings);

      if (loopBusinessIsPlatformOwner()) {
        // One verified Admin SDK operation updates both the dedicated settings
        // document and the root business mirror. This avoids partial saves and
        // bypasses client rules only for the platform owner.
        await callLoopOwnerBusinessAdmin({
          db,
          action: "saveNotificationSettings",
          businessId,
          payload: {
            settings: normalized,
            source: "business_settings_messages",
          },
        });
      } else {
        await loopBusinessSetDoc(db, `businesses/${businessId}/notificationSettings/main`, {
          ...normalized,
          updatedAt: loopBusinessNow(),
          source: "business_settings_messages",
        }, { merge: true });

        await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
          notificationSettings: normalized,
          onlineBookingRequiresApproval: normalized.onlineBookingRequiresApproval === true,
          updatedAt: loopBusinessNow(),
        });
      }

      setDirty(false);
      setStatus("Mensajes guardados correctamente.");
      return { ok: true, message: "Mensajes guardados correctamente." };
    } catch (err) {
      console.error("[NotificationSection] save failed", err);
      setStatus("No se pudo guardar. Revisa permisos o conexión.");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [businessId, db, settings, loading, saving]);

  const refreshWhatsAppHealth = useCallback(async () => {
    if (!businessId) return;
    setWhatsappHealthLoading(true);
    setWhatsappHealthError("");
    try {
      const fn = httpsCallable(getFunctions(), "getBusinessWhatsAppHealth");
      const res = await fn({ businessId });
      if(currentBusiness.current===businessId)setWhatsappHealth(res?.data || null);
    } catch (err) {
      console.error("[NotificationSection] WhatsApp health failed", err);
      if(currentBusiness.current===businessId)setWhatsappHealthError(err?.message || "No se pudo leer el estado de WhatsApp.");
    } finally {
      if(currentBusiness.current===businessId)setWhatsappHealthLoading(false);
    }
  }, [businessId]);

  const refreshWhatsAppMessages = useCallback(async () => {
    if (!businessId) return;
    setWhatsappMessagesLoading(true);
    setWhatsappMessagesError("");
    try {
      const fn = httpsCallable(getFunctions(), "getBusinessWhatsAppMessages");
      const res = await fn({ businessId, limit: 100 });
      if(currentBusiness.current===businessId)setWhatsappMessages(res?.data || null);
    } catch (err) {
      console.warn("[NotificationSection] WhatsApp messages failed", err);
      if(currentBusiness.current===businessId)setWhatsappMessagesError(err?.message || "No se pudo cargar el centro de mensajes WhatsApp.");
    } finally {
      if(currentBusiness.current===businessId)setWhatsappMessagesLoading(false);
    }
  }, [businessId]);

  const refreshWhatsAppUsage = useCallback(async () => {
    if (!businessId) return;
    setWhatsappUsageLoading(true);
    setWhatsappUsageError("");
    try {
      const fn = httpsCallable(getFunctions(), "getBusinessWhatsAppUsage");
      const res = await fn({ businessId });
      if(currentBusiness.current===businessId)setWhatsappUsage(res?.data || null);
    } catch (err) {
      console.warn("[NotificationSection] WhatsApp usage failed", err);
      if(currentBusiness.current===businessId)setWhatsappUsageError(err?.message || "No se pudo cargar el consumo WhatsApp.");
    } finally {
      if(currentBusiness.current===businessId)setWhatsappUsageLoading(false);
    }
  }, [businessId]);

  useEffect(()=>{setWhatsappHealth(null);setWhatsappMessages(null);setWhatsappHealthError("");setWhatsappMessagesError("");setActivityLimit(20);setActivitySearch("");setActivityStatusFilter("all");},[businessId]);
  useEffect(()=>{if(activeTab==="channels"||activeTab==="automations")refreshWhatsAppHealth();if(activeTab==="activity")refreshWhatsAppMessages();},[activeTab,refreshWhatsAppHealth,refreshWhatsAppMessages]);
  useEffect(() => {
    if (!externalSaveRef || activeTab === "receipts") return undefined;
    externalSaveRef.current = saveSettings;
    return () => {
      if (externalSaveRef.current === saveSettings) externalSaveRef.current = null;
    };
  }, [externalSaveRef, saveSettings, activeTab]);

  const waState=settings.whatsappEnabled===false?'WhatsApp pausado por el negocio':whatsappHealthLoading?'Verificando WhatsApp…':whatsappHealthError?'No se pudo verificar WhatsApp':whatsappHealth?.usage?.limitReached?'WhatsApp: límite mensual alcanzado':whatsappHealth?.config?.hasAccessToken===false||whatsappHealth?.config?.hasPhoneNumberId===false?'WhatsApp: falta configuración de Loop':!settings.whatsappTemplateRemindersLive&&(settings.whatsappReminder24hEnabled||settings.whatsappReminder2hEnabled)?'WhatsApp: recordatorios bloqueados por configuración anterior':whatsappHealth?.config?'WhatsApp configurado':'WhatsApp pendiente de verificación';
  const recommendedChanges=Object.keys(recommendedMessages).filter(key=>settings[key]!==recommendedMessages[key]);
  const rows=Array.isArray(whatsappMessages?.rows)?whatsappMessages.rows:[];
  const activityRows=rows.filter(row=>(activityStatusFilter==="all"||whatsappMessageEffectiveStatus(row)===activityStatusFilter)&&[row.customerName,row.clientName,row.toMasked,row.type,row.templateName,row.bookingId].filter(Boolean).join(" ").toLowerCase().includes(activitySearch.trim().toLowerCase()));
  const automationRows = [
    {
      icon: "calendar",
      title: "Solicitud recibida",
      copy: "Se envía cuando una reserva entra pendiente.",
      keys: ["sendCustomerEmailOnRequest", "popupNewBookingInApp"],
      channels: [
        { type: "email", key: "sendCustomerEmailOnRequest", label: "Email" },
      ],
    },
    {
      icon: "check",
      title: "Confirmación de reserva",
      copy: "Se envía cuando el negocio confirma la cita.",
      keys: ["sendCustomerEmailOnApproval", "whatsappAutoConfirmEnabled"],
      channels: [
        { type: "email", key: "sendCustomerEmailOnApproval", label: "Email" },
        { type: "whatsapp", key: "whatsappAutoConfirmEnabled", label: "WhatsApp" },
      ],
    },
    {
      icon: "cancel",
      title: "Cancelación de reserva",
      copy: "Se envía cuando una reserva se cancela.",
      keys: ["sendCustomerEmailOnCancellation", "whatsappAutoCancellationEnabled"],
      channels: [
        { type: "email", key: "sendCustomerEmailOnCancellation", label: "Email" },
        { type: "whatsapp", key: "whatsappAutoCancellationEnabled", label: "WhatsApp" },
      ],
    },
    {
      icon: "clock",
      title: "Recordatorio 24 horas antes",
      copy: "Se envía un día antes de la cita.",
      keys: ["customerReminder24h", "whatsappReminder24hEnabled"],
      channels: [
        { type: "email", key: "customerReminder24h", label: "Email" },
        { type: "whatsapp", key: "whatsappReminder24hEnabled", label: "WhatsApp" },
      ],
    },
    {
      icon: "clock",
      title: "Recordatorio 2 horas antes",
      copy: "Se envía cerca de la cita para recordar el horario.",
      keys: ["customerReminder2h", "whatsappReminder2hEnabled", "workerReminder2h"],
      channels: [
        { type: "email", key: "customerReminder2h", label: "Email" },
        { type: "whatsapp", key: "whatsappReminder2hEnabled", label: "WhatsApp" },
      ],
    },
  ];

  automationRows.push({title:'Calificación después de la cita',copy:'Desde 30 minutos después de terminar. Una solicitud por cita, con enlace para calificar al negocio.',channels:[{key:'whatsappReviewRequestEnabled',label:'WhatsApp'}]});
  automationRows.splice(2,0,{title:"Solicitud rechazada",copy:"Cuando el negocio rechaza la solicitud.",channels:[{key:"sendCustomerEmailOnRejection",label:"Email"}]});
  const teamRows=[["Aviso al dueño","notifyOwnerOnNewBooking","Cuando llega una nueva reserva."],["Aviso a administradores","notifyAdminsOnNewBooking","Cuando llega una nueva reserva."],["Aviso al trabajador asignado","notifyAssignedWorker","Notificación de la reserva al responsable."],["Aviso dentro de la agenda","popupNewBookingInApp","Muestra un aviso de nueva reserva en la aplicación."],["Recordatorio al trabajador","workerReminder2h","Dos horas antes de la cita."]];
  const brazilPending = String(businessDoc.countryCode || "").toUpperCase() === "BR";
  const tabs=[{id:"automations",label:"Automáticos"},{id:"receipts",label:"Comprobantes"},{id:"channels",label:"Canales"},{id:"activity",label:"Historial"}];
  return <div className="notify-settings-page msg-page msg-clean"><style>{styles}</style>
    {brazilPending && <div className="mc-readiness" role="status"><strong>{t("Pendiente de activación para Brasil")}</strong><p>{t("Puedes guardar tus preferencias. Los WhatsApp automáticos para Brasil se activarán después de aprobar y verificar las plantillas en portugués.")}</p></div>}
    <nav className="mc-tabs" aria-label={t("Secciones de mensajes")}>{tabs.map(tab=><button type="button" key={tab.id} aria-current={activeTab===tab.id?"page":undefined} onClick={()=>setActiveTab(tab.id)}><span aria-hidden="true"><MessageIcon name={({automations:"clock",receipts:"book",channels:"message",activity:"chart"})[tab.id]}/></span>{t(tab.label)}</button>)}</nav>
    {status&&activeTab!=="receipts"&&<p role="status">{t(status)}</p>}
    {!externalSaveRef&&!hideLocalActions&&activeTab!=="receipts"&&<button type="button" disabled={loading||saving} onClick={()=>saveSettings().catch(()=>{})}>{saving?t("Guardando…"):t("Guardar cambios")}</button>}
    <div hidden={activeTab!=="receipts"}><PaymentReceiptSettings businessId={businessId} db={db} active={activeTab==="receipts"} externalSaveRef={externalSaveRef}/></div>
    {activeTab==="automations"&&<section className="mc-panel"><h3>{t("Mensajes a tus clientes")}</h3><p>{t("Elige qué enviar y revisa su estado. Los cambios se aplican al guardar.")}</p>
      <div className="mc-readiness" role="status"><strong>{t(brazilPending?"Pendiente de activación para Brasil":waState)}{dirty?" · cambios sin guardar":""}</strong><p>{t("El envío respeta las preferencias del cliente, su teléfono, el plan y la disponibilidad de las plantillas. Configurado no significa entregado.")}</p><button type="button" disabled={whatsappHealthLoading} onClick={refreshWhatsAppHealth}>{t("Verificar canal")}</button></div>
      <button type="button" disabled={loading||saving} onClick={()=>setShowRecommended(v=>!v)}>{t("Aplicar configuración recomendada")}</button>
      {showRecommended&&<div className="mc-recommended"><h4>{t("Revisar antes de aplicar")}</h4><p>{t("Se activarán correos de reserva, confirmación y cancelación por WhatsApp, recordatorios de 24 h y 2 h y avisos al equipo. Esto también habilita WhatsApp si estaba pausado. No activa campañas ni reenvía mensajes anteriores.")}</p><p>{recommendedChanges.length?t('{count} ajustes cambiarán. Las opciones apagadas se encenderán solo si aplicas y guardas.',{count:recommendedChanges.length}):t('Ya tienes estas preferencias activadas.')}</p><ul>{recommendedChanges.map(key=><li key={key}>{({whatsappTemplateRemindersLive:'Completar la activación interna de recordatorios',whatsappEnabled:'Habilitar WhatsApp',whatsappManualMessagesEnabled:'Habilitar mensajes manuales'})[key]||automationRows.flatMap(row=>row.channels.map(channel=>({key:channel.key,label:row.title+' · '+channel.label}))).find(item=>item.key===key)?.label||teamRows.find(row=>row[1]===key)?.[0]||'Avisos de reservas'}</li>)}</ul><p>{t("Los comprobantes se configuran por separado en su pestaña.")}</p><button type="button" disabled={loading||saving||!recommendedChanges.length} onClick={()=>{setMany(recommendedMessages);setShowRecommended(false);}}>{t("Aplicar al borrador")}</button><button type="button" onClick={()=>setShowRecommended(false)}>{t("Conservar configuración")}</button></div>}
      {!settings.whatsappEnabled&&<p className="mc-note">{t("WhatsApp está deshabilitado. Puedes habilitarlo en Canales; tus preferencias se conservan.")}</p>}
      <fieldset disabled={loading||saving} className="mc-list"><legend className="mc-sr">{t("Canales por evento")}</legend>{automationRows.map(row=><div className="mc-row" key={row.title}><div><strong>{t(row.title)}</strong><p>{t(row.copy)}</p></div><div className="mc-options">{row.channels.map(channel=>{const state=brazilPending && channel.key.startsWith("whatsapp") ? {tone:"blocked",label:"Pendiente de activación para Brasil"} : messageReadiness(settings,channel.key,whatsappHealthError?null:whatsappHealth);return <div className="mc-channel-state" key={channel.key}><label><input type="checkbox" aria-label={`${channel.label}: ${t(row.title)}`} checked={settings[channel.key]===true} onChange={e=>setValue(channel.key,e.target.checked)}/>{channel.label}</label><small className={state.tone}>{t(state.label)}{dirty&&settings[channel.key]===true?t(" · sin guardar"):""}</small>{!brazilPending&&state.tone==="blocked"&&channel.key.startsWith("whatsappReminder")&&!settings.whatsappTemplateRemindersLive&&<button type="button" onClick={()=>setValue(channel.key,true)}>{t("Completar activación")}</button>}</div>;})}</div></div>)}</fieldset>
      <h3 className="mc-group">{t("Avisos a tu equipo")}</h3><p>{t("Estas preferencias son independientes de los mensajes al cliente.")}</p>
      <fieldset disabled={loading||saving} className="mc-list"><legend className="mc-sr">{t("Avisos internos")}</legend>{teamRows.map(([title,key,copy])=><label className="mc-row" key={key}><span><strong>{t(title)}</strong><p>{t(copy)}</p></span><input type="checkbox" aria-label={t(title)} checked={settings[key]===true} onChange={e=>setValue(key,e.target.checked)}/></label>)}</fieldset>
      {loading&&<p role="status">{t("Cargando preferencias…")}</p>}
    </section>}
    {activeTab==="channels"&&<section className="mc-panel"><h3>{t("Canales")}</h3><p>{t("Las preferencias de envío y la disponibilidad del canal son cosas distintas.")}</p>
      <div className="mc-row"><div><strong>{t("Email")}</strong><p>{t("Selecciona los correos en Automáticos y Comprobantes.")}</p></div><span>{t("Entrega no verificada aquí")}</span></div>
      <div className="mc-row"><div><strong>WhatsApp</strong><p>{t("Control general para los mensajes de WhatsApp del negocio.")}</p></div><label className="mc-options"><input type="checkbox" disabled={loading||saving} checked={settings.whatsappEnabled===true} onChange={e=>setValue("whatsappEnabled",e.target.checked)}/>{t("Habilitar WhatsApp")}</label></div>
      <fieldset disabled={loading||saving} className="mc-list"><legend>{t("Opciones de WhatsApp")}</legend>{[["whatsappManualMessagesEnabled","Mensajes manuales"]].map(([key,label])=><label className="mc-row" key={key}><span>{label}</span><input type="checkbox" checked={settings[key]===true} onChange={e=>setValue(key,e.target.checked)}/></label>)}</fieldset>
      <p className="mc-note">{t("Loop administra la configuración técnica de las plantillas. Activa cada recordatorio directamente en Automáticos.")}</p><details className="mc-details"><summary>{t("Revisar estado de WhatsApp")}</summary><button type="button" disabled={whatsappHealthLoading} onClick={()=>refreshWhatsAppHealth()}>{whatsappHealthLoading?"Consultando…":"Actualizar diagnóstico"}</button>
      {whatsappHealthError&&<p role="alert">{t("No pudimos verificar WhatsApp. Intenta nuevamente.")}</p>}
      {Array.isArray(whatsappHealth?.checks)&&whatsappHealth.checks.map((check,i)=><p key={i}>{check.label||check.name||check.key}: {check.ok===true?"Correcto":"Revisar"}</p>)}
      {!whatsappHealthLoading&&!whatsappHealthError&&!whatsappHealth?.checks?.length&&<p>{t("Sin diagnóstico disponible.")}</p>}</details>
      <div className="mc-row"><div><strong>{t("Avisos en la aplicación")}</strong><p>{t("Se configuran en Automáticos → Avisos a tu equipo. La recepción depende de los permisos del dispositivo.")}</p></div></div>
    </section>}
    {activeTab==="activity"&&<section className="mc-panel"><div className="mc-heading"><div><h3>{t("Historial de WhatsApp")}</h3><p>{t("Hasta 100 registros recientes. Los correos y comprobantes aún no se incluyen en esta vista.")}</p></div><button type="button" disabled={whatsappMessagesLoading} onClick={()=>refreshWhatsAppMessages()}>{t("Actualizar")}</button></div>
      <div className="mc-filters"><label>{t("Buscar")}<input type="search" placeholder={t("Cliente, teléfono o reserva")} value={activitySearch} onChange={e=>{setActivitySearch(e.target.value);setActivityLimit(20);}}/></label><label>{t("Estado")}<select value={activityStatusFilter} onChange={e=>{setActivityStatusFilter(e.target.value);setActivityLimit(20);}}><option value="all">{t("Todos")}</option><option value="sent">{t("Enviado")}</option><option value="delivered">{t("Entregado")}</option><option value="read">{t("Leído")}</option><option value="failed">{t("Fallido")}</option><option value="skipped">{t("Omitido")}</option></select></label></div>
      {whatsappMessagesLoading?<p role="status">{t("Cargando historial…")}</p>:whatsappMessagesError?<p role="alert">{t("No se pudo cargar el historial. Usa Actualizar para reintentar.")}</p>:<>
        <p>{activityRows.length}{" " + t("registros disponibles en esta búsqueda")}</p>
        {!activityRows.length&&<p className="mc-note">{t("No hay mensajes en los registros consultados que coincidan con esta búsqueda.")}</p>}
        {activityRows.slice(0,activityLimit).map((row,index)=><details className="mc-history" key={row.id||row.metaMessageId||index}><summary><span><strong>{row.customerName||row.clientName||row.toMasked||t("Cliente")}</strong><small>{whatsappMessageTypeLabel(row.templateName||row.type||row.reason)}</small></span><span>{healthStatusLabel(whatsappMessageEffectiveStatus(row))}</span><time>{formatHealthDate(row.createdAt)}</time></summary><div><p>{t("Reserva:") + " "}{row.bookingId||"Sin referencia"}</p><p>{t("Última actualización:") + " "}{formatHealthDate(row.providerStatusUpdatedAt||row.createdAt)}</p><p>{whatsappMessageStatusCopy(row)}</p><p>{t("Enviado indica aceptación del envío; entregado requiere confirmación del proveedor.")}</p></div></details>)}
        {activityRows.length>activityLimit&&<button type="button" onClick={()=>setActivityLimit(v=>v+20)}>{t("Mostrar más")}</button>}
      </>}
    </section>}
  </div>;
}




const styles = `
.msg-clean .mc-readiness,.msg-clean .mc-recommended{padding:16px;margin:16px 0;background:#f3f8f6;border:1px solid #d5e4df;border-radius:10px}.msg-clean .mc-recommended button{margin-right:10px}.msg-clean .mc-channel-state{display:grid;gap:6px;max-width:190px}.msg-clean .mc-channel-state small{font-size:11px;line-height:1.4;max-width:180px;white-space:normal}.msg-clean .mc-channel-state .blocked{color:#975019}.msg-clean .mc-channel-state .ready{color:#00695f}.msg-clean .mc-channel-state .muted,.msg-clean .mc-channel-state .unknown{color:#61736d}.msg-clean .mc-options{flex-wrap:wrap}@media(max-width:600px){.msg-clean .mc-options{width:100%}.msg-clean .mc-channel-state{flex:1;min-width:110px}.msg-clean .mc-row{flex-wrap:wrap}}


.msg-clean{--mc-ink:#173334;--mc-muted:#627777;--mc-line:#e3ebe9;--mc-accent:#00776b}
.msg-clean .mc-tabs{display:flex;gap:4px;overflow-x:auto;padding:5px;background:#edf3f1;border:1px solid #e2eae7;border-radius:11px;justify-self:start;max-width:100%;box-sizing:border-box}
.msg-clean .mc-tabs button{display:flex;align-items:center;gap:8px;white-space:nowrap;background:transparent;border:1px solid transparent;border-radius:8px;padding:10px 16px;color:#607572;font-size:13px;font-weight:600;cursor:pointer;transition:background .15s,color .15s}
.msg-clean .mc-tabs button[aria-current]{background:#fff;border-color:#dce7e3;color:#00695e;box-shadow:0 2px 5px #153e3110}
.msg-clean .mc-tabs .msg-icon{width:17px!important;height:17px!important;background:transparent!important;padding:0!important;border:0!important;box-shadow:none!important;color:inherit}
.msg-clean .mc-tabs .msg-icon svg{width:17px;height:17px}
.msg-clean .mc-tabs button:hover{color:#00695e;background:#ffffffa6}
.msg-clean .mc-panel{background:white;border:1px solid var(--mc-line);border-radius:14px;padding:24px 28px;min-width:0;box-shadow:0 5px 20px #254a3e05;color:var(--mc-ink)}
.msg-clean .mc-panel h3{font-size:17px;font-weight:650;letter-spacing:-.025em;margin:0 0 7px;color:var(--mc-ink)}
.msg-clean .mc-panel p{font-size:13px;font-weight:400;color:var(--mc-muted);line-height:1.55;margin:5px 0 16px}
.msg-clean .mc-list{border:0;padding:0;margin:18px 0 0;min-width:0}
.msg-clean .mc-list legend:not(.mc-sr){font-size:12px;font-weight:600;color:var(--mc-muted);padding:12px 0 0}
.msg-clean .mc-row{display:flex;align-items:center;justify-content:space-between;gap:24px;padding:15px 0;border-bottom:1px solid #edf1ef;font-size:13px;min-width:0}
.msg-clean .mc-row:last-child{border-bottom:0}
.msg-clean .mc-row strong{font-size:13px;font-weight:600;letter-spacing:-.01em;color:var(--mc-ink)}
.msg-clean .mc-row p{margin:4px 0 0;font-size:12px}
.msg-clean .mc-row>div:first-child,.msg-clean .mc-row>span:first-child{min-width:0;overflow-wrap:anywhere}
.msg-clean .mc-options{display:flex;gap:8px;flex-shrink:0;align-items:center}
.msg-clean .mc-options label,.msg-clean label.mc-options{display:flex;gap:7px;align-items:center;font-size:12px;font-weight:500;padding:8px 10px;border:1px solid #e2e9e6;border-radius:7px;color:#637772;background:#fff;cursor:pointer;transition:background .15s,border-color .15s}
.msg-clean .mc-options label:has(input:checked),.msg-clean label.mc-options:has(input:checked){color:#006c5f;background:#eff8f4;border-color:#bcded1}
.msg-clean input[type=checkbox]{width:17px;height:17px;accent-color:#00776b;flex-shrink:0;margin:0;cursor:pointer}
.msg-clean label.mc-row>input[type=checkbox]{appearance:none;width:34px;height:20px;border-radius:12px;background:#dbe4e0;position:relative;transition:background .15s;border:1px solid #ccd9d3}
.msg-clean label.mc-row>input[type=checkbox]::after{content:"";position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;background:#fff;box-shadow:0 1px 3px #153e3126;transition:transform .15s}
.msg-clean label.mc-row>input[type=checkbox]:checked{background:#00776b;border-color:#00776b}
.msg-clean label.mc-row>input[type=checkbox]:checked::after{transform:translateX(14px)}
.msg-clean input:disabled,.msg-clean button:disabled{opacity:.5;cursor:wait}
.msg-clean .mc-group{margin-top:26px!important;padding-top:24px;border-top:1px solid var(--mc-line)}
.msg-clean .mc-note{background:#f5f8f6;padding:11px 14px;border-left:3px solid #bdd7cb;border-radius:0 6px 6px 0}
.msg-clean .mc-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%)}
.msg-clean .mc-heading{display:flex;justify-content:space-between;gap:16px;align-items:start}
.msg-clean .mc-panel button{padding:9px 13px;border:1px solid #d4e1db;border-radius:7px;background:white;color:#006d62;font-size:12px;font-weight:600;cursor:pointer;transition:background .15s}
.msg-clean .mc-panel button:hover{background:#f0f7f3}
.msg-clean .mc-filters{display:flex;gap:12px;margin:20px 0 16px;padding:14px;background:#f7f9f8;border-radius:9px;flex-wrap:wrap}
.msg-clean .mc-filters label{display:grid;gap:7px;font-size:11px;font-weight:600;color:#627772;flex:1;min-width:160px}
.msg-clean .mc-filters label:first-child{flex:3}
.msg-clean .mc-filters input,.msg-clean .mc-filters select{padding:10px 12px;border:1px solid #dce6e0;border-radius:7px;width:100%;box-sizing:border-box;background:white;font-size:13px;color:#29413b;min-height:39px}
.msg-clean .mc-history{border-bottom:1px solid #e8eeea;padding:0}
.msg-clean .mc-history summary{display:grid;grid-template-columns:minmax(0,1fr) 100px 160px 14px;gap:14px;align-items:center;cursor:pointer;font-size:12px;padding:14px 8px;list-style:none}
.msg-clean .mc-history summary::-webkit-details-marker,.msg-clean .mc-details summary::-webkit-details-marker{display:none}
.msg-clean .mc-history summary::after,.msg-clean .mc-details summary::after{content:"";width:6px;height:6px;border-right:1.5px solid #6b8278;border-bottom:1.5px solid #6b8278;transform:rotate(45deg);justify-self:end;transition:transform .15s}
.msg-clean .mc-history[open] summary::after,.msg-clean .mc-details[open] summary::after{transform:rotate(225deg)}
.msg-clean .mc-history summary:hover{background:#f7faf8}
.msg-clean .mc-history strong{font-weight:600;font-size:13px}
.msg-clean .mc-history small{display:block;color:#72837b;margin-top:4px;font-size:11px}
.msg-clean .mc-history time{color:#72837b;font-size:11px}
.msg-clean .mc-history[open]{background:#f7faf8;border-radius:7px}
.msg-clean .mc-history>div{padding:10px 16px;overflow-wrap:anywhere;border-top:1px dashed #dce7df}
.msg-clean .mc-details{padding:16px 0;font-size:13px}.msg-clean .mc-details summary{display:flex;align-items:center;justify-content:space-between;cursor:pointer;margin-bottom:12px;list-style:none;color:#00776b;font-weight:500}
.msg-clean :focus-visible{outline:2px solid #008577;outline-offset:3px}
.msg-clean>p[role=status]{font-size:12px;color:#006d62;margin:0}
.msg-clean .mc-receipt-copy{display:block;font-size:12px;color:var(--mc-muted);line-height:1.55;margin-top:4px}
@media(max-width:640px){.msg-clean .mc-panel{padding:18px 16px}.msg-clean .mc-tabs{justify-self:stretch}.msg-clean .mc-tabs button{padding:9px 11px;font-size:12px}.msg-clean .mc-tabs .msg-icon{display:none}.msg-clean .mc-row{flex-wrap:wrap;gap:10px}.msg-clean .mc-options{flex-wrap:wrap}.msg-clean label.mc-row{flex-wrap:nowrap}.msg-clean .mc-history summary{grid-template-columns:1fr auto 12px;gap:8px}.msg-clean .mc-history time{grid-column:1/3;grid-row:2}.msg-clean .mc-history summary::after{grid-column:3;grid-row:1}.msg-clean .mc-heading{flex-wrap:wrap}.msg-clean .mc-filters{padding:10px}}
@media(prefers-reduced-motion:reduce){.msg-clean *{transition:none!important}}

  .notify-settings-page.msg-page{
    display:grid;
    gap:18px;
    color:#0f172a;
    width:100%;
  }

  .msg-shell{
    border:1px solid rgba(226,232,240,.92);
    border-radius:30px;
    background:rgba(255,255,255,.92);
    box-shadow:0 18px 54px rgba(15,23,42,.055);
    overflow:hidden;
  }

  .msg-header{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:18px;
    padding:28px 30px 20px;
  }

  .msg-header h2{
    margin:0;
    color:#0f172a;
    font-size:clamp(26px,2.2vw,34px);
    line-height:1;
    font-weight:950;
    letter-spacing:-.055em;
  }

  .msg-header p{
    margin:10px 0 0;
    color:#475569;
    font-size:14px;
    line-height:1.45;
    font-weight:750;
  }

  .msg-local-save{
    display:grid;
    justify-items:end;
    gap:8px;
    flex:0 0 auto;
  }

  .msg-local-save button{
    min-height:46px;
    border:0;
    border-radius:15px;
    background:linear-gradient(135deg,#005f57,#007467);
    color:#fff;
    padding:0 20px;
    font-size:14px;
    font-weight:950;
    box-shadow:0 18px 34px rgba(0,103,95,.2);
    cursor:pointer;
  }

  .msg-local-save button:disabled{opacity:.58;cursor:not-allowed}
  .msg-local-save span,.msg-inline-status{
    color:#64748b;
    font-size:12px;
    font-weight:850;
  }

  .msg-inline-status-floating{
    display:block;
    padding:14px 30px 0;
  }

  .msg-tabs{
    display:flex;
    gap:34px;
    padding:8px 30px 0;
    border-bottom:1px solid rgba(226,232,240,.86);
  }

  .msg-tabs button{
    min-height:58px;
    border:0;
    border-bottom:3px solid transparent;
    background:transparent;
    color:#334155;
    font-size:14px;
    font-weight:850;
    cursor:pointer;
    padding:0;
  }

  .msg-tabs button.active{
    color:#00675f;
    border-bottom-color:#00675f;
  }

  .msg-tab-content{padding:24px 30px 30px;}
  .msg-tab-panel{display:grid;gap:22px;}

  .msg-automation-hero{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:20px;
    border:1px solid rgba(226,232,240,.92);
    border-radius:24px;
    background:linear-gradient(135deg,#fff,#fbf8f0);
    padding:22px 24px;
    box-shadow:0 14px 36px rgba(15,23,42,.045);
  }

  .msg-hero-copy{display:flex;align-items:center;gap:18px;min-width:0;}
  .msg-hero-copy>.msg-icon{width:56px;height:56px;}
  .msg-hero-copy h3{margin:0;color:#0f172a;font-size:18px;font-weight:950;letter-spacing:-.025em;}
  .msg-hero-copy p{margin:6px 0 0;color:#64748b;font-size:13px;font-weight:750;line-height:1.42;}

  .msg-channel-strip{
    display:flex;
    align-items:center;
    gap:18px;
    flex:0 0 auto;
  }

  .msg-channel-strip>div{
    min-width:130px;
    display:grid;
    grid-template-columns:38px minmax(0,1fr);
    column-gap:10px;
    align-items:center;
    padding-left:18px;
    border-left:1px solid rgba(226,232,240,.86);
  }

  .msg-channel-strip .msg-icon{grid-row:span 2;width:38px;height:38px;}
  .msg-channel-strip strong{font-size:14px;font-weight:950;color:#0f172a;}
  .msg-channel-strip span{font-size:12px;font-weight:900;color:#94a3b8;}
  .msg-channel-strip span.active{color:#00836f;}

  .msg-content-grid{
    display:grid;
    grid-template-columns:minmax(0,1fr) 390px;
    gap:22px;
    align-items:start;
  }

  .msg-main-card,.msg-side-card,.msg-channel-card,.msg-security-card,.msg-upgrade-card{
    border:1px solid rgba(226,232,240,.92);
    background:#fff;
    border-radius:24px;
    box-shadow:0 14px 36px rgba(15,23,42,.045);
  }

  .msg-main-card{overflow:hidden;}
  .msg-card-title{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:22px 24px 12px;}
  .msg-card-title h3,.msg-side-card h3,.msg-section-intro h3{
    margin:0;
    color:#0f172a;
    font-size:18px;
    font-weight:950;
    letter-spacing:-.03em;
  }

  .msg-card-title p,.msg-section-intro p{
    margin:6px 0 0;
    color:#64748b;
    font-size:13px;
    font-weight:750;
    line-height:1.4;
  }

  .msg-moments-list{display:grid;}
  .msg-moment-row{
    display:grid;
    grid-template-columns:48px minmax(0,1fr) auto 58px;
    align-items:center;
    gap:16px;
    padding:18px 22px;
    border-top:1px solid rgba(226,232,240,.82);
  }

  .msg-moment-row>.msg-icon{width:44px;height:44px;}
  .msg-moment-copy strong{display:block;color:#0f172a;font-size:14px;font-weight:950;}
  .msg-moment-copy span{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;}

  .msg-moment-channels{
    display:flex;
    align-items:center;
    gap:10px;
  }

  .msg-channel-button{
    width:34px;
    height:34px;
    border:0;
    border-radius:999px;
    display:grid;
    place-items:center;
    background:transparent;
    color:#94a3b8;
    cursor:pointer;
    opacity:.54;
    transition:transform .16s ease, opacity .16s ease, background .16s ease;
  }

  .msg-channel-button .msg-icon{
    width:30px;
    height:30px;
    background:transparent;
    box-shadow:none;
  }

  .msg-channel-button.active{
    opacity:1;
    color:#00675f;
    background:rgba(0,103,95,.06);
  }

  .msg-channel-button.whatsapp.active{color:#10b981;background:rgba(16,185,129,.08);}
  .msg-channel-button.push.active{color:#00675f;}
  .msg-channel-button:hover{transform:translateY(-1px);}

  .msg-toggle{
    width:52px;
    height:30px;
    border:0;
    border-radius:999px;
    background:#cbd5e1;
    padding:3px;
    cursor:pointer;
    transition:background .16s ease;
  }

  .msg-toggle span{
    display:block;
    width:24px;
    height:24px;
    border-radius:999px;
    background:#fff;
    box-shadow:0 4px 12px rgba(15,23,42,.18);
    transition:transform .16s ease;
  }

  .msg-toggle.is-on{background:#00675f;}
  .msg-toggle.is-on span{transform:translateX(22px);}

  .msg-add-button{
    margin:16px 22px 22px;
    min-height:42px;
    border-radius:14px;
    border:1px solid rgba(0,103,95,.16);
    background:#fff;
    color:#00675f;
    padding:0 16px;
    font-size:13px;
    font-weight:950;
    display:inline-flex;
    align-items:center;
    gap:8px;
    width:max-content;
    cursor:pointer;
  }

  .msg-side-stack{display:grid;gap:18px;}
  .msg-side-card{padding:24px;}
  .msg-quick-view{display:grid;grid-template-columns:150px minmax(0,1fr);gap:16px;align-items:center;}
  .msg-quick-view h3{grid-column:1/-1;}

  .msg-donut{
    --msg-p: 60%;
    width:140px;
    height:140px;
    border-radius:999px;
    background:conic-gradient(#00675f var(--msg-p), #ccf1e9 0);
    display:grid;
    place-items:center;
    position:relative;
  }

  .msg-donut:after{
    content:"";
    position:absolute;
    inset:17px;
    border-radius:999px;
    background:#fff;
  }

  .msg-donut div{
    position:relative;
    z-index:1;
    text-align:center;
    display:grid;
    gap:3px;
  }

  .msg-donut strong{font-size:26px;font-weight:950;color:#0f172a;letter-spacing:-.04em;}
  .msg-donut span{font-size:12px;font-weight:750;color:#64748b;max-width:82px;line-height:1.25;}

  .msg-donut.large{width:154px;height:154px;margin:12px auto;}
  .msg-donut.large:after{inset:20px;}

  .msg-legend{display:grid;gap:14px;}
  .msg-legend span{display:grid;grid-template-columns:14px minmax(0,1fr) auto;gap:10px;align-items:center;color:#475569;font-size:13px;font-weight:800;}
  .msg-legend i{width:9px;height:9px;border-radius:999px;background:#00675f;}
  .msg-legend i.email{background:#00675f;}
  .msg-legend i.whatsapp{background:#10b981;}
  .msg-legend i.push{background:#60a5fa;}
  .msg-legend b{color:#0f172a;font-weight:900;}

  .msg-advice-title{display:flex;align-items:center;gap:12px;margin-bottom:12px;}
  .msg-advice-title .msg-icon{width:34px;height:34px;background:#fff7ed;color:#f59e0b;}
  .msg-side-card p{margin:0;color:#64748b;font-size:13px;font-weight:750;line-height:1.55;}
  .msg-side-card button,.msg-security-card button,.msg-upgrade-card button,.msg-channel-card-head button,.msg-stats-link,.msg-activity-intro button{
    border:0;
    background:transparent;
    color:#00675f;
    font-size:13px;
    font-weight:950;
    cursor:pointer;
    padding:0;
    display:inline-flex;
    align-items:center;
    gap:8px;
  }

  .msg-side-card button{margin-top:18px;}

  .msg-upgrade-card{
    display:flex;
    align-items:center;
    gap:16px;
    padding:18px 20px;
    background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);
    border-color:rgba(0,103,95,.18);
  }

  .msg-upgrade-card>.msg-icon{width:52px;height:52px;}
  .msg-upgrade-card strong{display:block;color:#0f172a;font-size:15px;font-weight:950;}
  .msg-upgrade-card p{margin:5px 0 0;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;}
  .msg-upgrade-card button{margin-left:auto;border:1px solid rgba(0,103,95,.14);border-radius:14px;padding:0 14px;height:42px;background:#fff;}

  .msg-section-intro{padding:0 4px;}
  .msg-channel-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px;}

  .msg-channel-card{padding:24px;display:grid;gap:20px;}
  .msg-channel-card-head{display:grid;grid-template-columns:60px minmax(0,1fr) auto;gap:16px;align-items:start;}
  .msg-channel-card-head>.msg-icon{width:56px;height:56px;}
  .msg-channel-card-head h3{margin:0;color:#0f172a;font-size:18px;font-weight:950;}
  .msg-channel-card-head p{margin:7px 0 14px;color:#64748b;font-size:12px;font-weight:750;line-height:1.45;}
  .msg-channel-card-head>span{
    border-radius:999px;
    background:#f1f5f9;
    color:#64748b;
    padding:6px 10px;
    font-size:11px;
    font-weight:950;
  }
  .msg-channel-card-head>span.active{background:#dcfce7;color:#047857;}
  .msg-channel-card-head button{
    height:36px;
    border:1px solid rgba(226,232,240,.94);
    border-radius:12px;
    padding:0 12px;
    background:#fff;
    color:#0f172a;
  }
  .msg-channel-card-head button .msg-icon{width:18px;height:18px;background:transparent;box-shadow:none;}

  .msg-channel-config{display:grid;gap:10px;border-top:1px solid rgba(226,232,240,.86);padding-top:16px;}
  .msg-channel-config strong{color:#0f172a;font-size:13px;font-weight:950;margin-bottom:2px;}
  .msg-channel-config div{display:flex;align-items:center;justify-content:space-between;gap:12px;}
  .msg-channel-config span{color:#64748b;font-size:12px;font-weight:750;}
  .msg-channel-config em{color:#0f172a;font-style:normal;font-size:12px;font-weight:850;text-align:right;overflow-wrap:anywhere;}

  .msg-stats-link{
    border-top:1px solid rgba(226,232,240,.86);
    padding-top:14px;
    justify-content:space-between;
  }
  .msg-stats-link .msg-icon{width:18px;height:18px;background:transparent;box-shadow:none;}

  .msg-security-card{
    display:flex;
    align-items:center;
    gap:16px;
    padding:18px 22px;
    background:linear-gradient(135deg,rgba(0,103,95,.06),#fff);
    border-color:rgba(0,103,95,.16);
  }
  .msg-security-card>.msg-icon{width:48px;height:48px;}
  .msg-security-card strong{display:block;color:#0f172a;font-size:15px;font-weight:950;}
  .msg-security-card p{margin:5px 0 0;color:#64748b;font-size:12px;font-weight:750;}
  .msg-security-card button{margin-left:auto;border:1px solid rgba(0,103,95,.14);border-radius:14px;padding:0 14px;height:40px;background:#fff;}

  .msg-activity-intro{display:flex;align-items:center;justify-content:space-between;gap:16px;}
  .msg-activity-intro button{border:1px solid rgba(0,103,95,.14);border-radius:14px;background:#fff;height:40px;padding:0 14px;}

  .msg-activity-grid{
    display:grid;
    grid-template-columns:repeat(5,minmax(0,1fr));
    gap:14px;
  }

  .msg-metric{
    position:relative;
    min-height:94px;
    border:1px solid rgba(226,232,240,.92);
    background:#fff;
    border-radius:20px;
    padding:18px;
    box-shadow:0 12px 30px rgba(15,23,42,.04);
    overflow:hidden;
  }

  .msg-metric strong{display:block;color:#0f172a;font-size:22px;font-weight:950;letter-spacing:-.03em;}
  .msg-metric span{display:block;margin-top:5px;color:#64748b;font-size:12px;font-weight:800;}
  .msg-metric .msg-icon{position:absolute;right:16px;top:18px;width:42px;height:42px;}
  .msg-metric.danger .msg-icon{background:#fee2e2;color:#dc2626;}

  .msg-activity-layout{
    display:grid;
    grid-template-columns:minmax(0,1fr) 360px;
    gap:22px;
    align-items:start;
  }

  .msg-activity-table-card{padding:0;}
  .msg-activity-toolbar{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:16px;
    padding:20px 22px;
    border-bottom:1px solid rgba(226,232,240,.86);
  }
  .msg-activity-toolbar h3{margin:0;color:#0f172a;font-size:18px;font-weight:950;}
  .msg-activity-toolbar>div{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
  .msg-activity-toolbar select,.msg-activity-toolbar span{
    height:38px;
    border:1px solid rgba(226,232,240,.94);
    background:#fff;
    border-radius:14px;
    padding:0 12px;
    color:#0f172a;
    font-size:12px;
    font-weight:850;
  }
  .msg-activity-toolbar span{display:inline-flex;align-items:center;color:#64748b;}

  .msg-activity-table{display:grid;}
  .msg-activity-table-head,.msg-activity-row{
    display:grid;
    grid-template-columns:minmax(220px,1.45fr) .7fr .8fr .7fr 1fr .8fr;
    gap:12px;
    align-items:center;
    padding:13px 22px;
  }
  .msg-activity-table-head{
    color:#64748b;
    background:#fbfdfe;
    border-bottom:1px solid rgba(226,232,240,.78);
    font-size:11px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.06em;
  }
  .msg-activity-row{
    border-bottom:1px solid rgba(226,232,240,.72);
    color:#334155;
    font-size:12px;
    font-weight:780;
  }
  .msg-activity-row:last-child{border-bottom:0;}
  .msg-activity-row>div:first-child{display:grid;grid-template-columns:34px minmax(0,1fr);column-gap:10px;align-items:center;}
  .msg-activity-row strong{display:block;color:#0f172a;font-size:13px;font-weight:950;}
  .msg-activity-row em{display:block;margin-top:3px;color:#64748b;font-style:normal;font-size:11px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .msg-mini-channel{width:32px;height:32px;border-radius:999px;display:grid;place-items:center;background:rgba(0,103,95,.08);color:#00675f;}
  .msg-mini-channel.whatsapp{color:#10b981;background:#dcfce7;}
  .msg-mini-channel .msg-icon{width:22px;height:22px;background:transparent;box-shadow:none;}

  .msg-status-pill{
    width:max-content;
    border-radius:999px;
    padding:5px 9px;
    font-size:11px;
    font-weight:950;
    background:#e2e8f0;
    color:#475569;
  }
  .msg-status-pill.read{background:#e0f2fe;color:#0369a1;}
  .msg-status-pill.delivered{background:#dcfce7;color:#166534;}
  .msg-status-pill.sent{background:#ecfdf5;color:#047857;}
  .msg-status-pill.failed{background:#fee2e2;color:#991b1b;}
  .msg-status-pill.skipped{background:#fef3c7;color:#92400e;}

  .msg-distribution{padding:24px;}
  .msg-distribution h3{margin-bottom:8px;}

  .msg-empty-row{
    padding:28px 22px;
    color:#64748b;
    font-size:13px;
    font-weight:850;
    text-align:center;
  }

  .msg-error-box{
    border:1px solid #fecaca;
    background:#fff7f7;
    color:#991b1b;
    border-radius:18px;
    padding:13px 15px;
    font-size:13px;
    font-weight:850;
  }

  .msg-icon{
    width:34px;
    height:34px;
    border-radius:999px;
    display:inline-grid;
    place-items:center;
    background:rgba(0,103,95,.09);
    color:#00675f;
    flex:0 0 auto;
  }
  .msg-icon svg{width:58%;height:58%;}
  .msg-icon-email{background:#e8f3f1;color:#00675f;}
  .msg-icon-whatsapp{background:#dcfce7;color:#10b981;}
  .msg-icon-push{background:#e8f3f1;color:#00675f;}
  .msg-icon-calendar,.msg-icon-clock,.msg-icon-check,.msg-icon-message{background:#e8f7f2;color:#00675f;}
  .msg-icon-cancel,.msg-icon-alert{background:#fff1f2;color:#e11d48;}
  .msg-icon-crown{background:#fff7ed;color:#d97706;}

  @media (max-width: 1180px){
    .msg-content-grid,.msg-activity-layout{grid-template-columns:1fr;}
    .msg-side-stack{grid-template-columns:repeat(2,minmax(0,1fr));}
    .msg-upgrade-card{grid-column:1/-1;}
    .msg-channel-cards{grid-template-columns:1fr;}
  }

  @media (max-width: 900px){
    .msg-header{flex-direction:column;}
    .msg-tabs{gap:20px;overflow:auto;}
    .msg-tab-content{padding:18px;}
    .msg-automation-hero{align-items:flex-start;flex-direction:column;}
    .msg-channel-strip{width:100%;justify-content:space-between;gap:10px;}
    .msg-channel-strip>div{min-width:0;padding-left:0;border-left:0;}
    .msg-moment-row{grid-template-columns:44px minmax(0,1fr) 58px;}
    .msg-moment-channels{grid-column:2/-1;}
    .msg-side-stack{grid-template-columns:1fr;}
    .msg-activity-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
    .msg-activity-table{overflow:auto;}
    .msg-activity-table-head,.msg-activity-row{min-width:900px;}
    .msg-security-card,.msg-upgrade-card{align-items:flex-start;flex-direction:column;}
    .msg-security-card button,.msg-upgrade-card button{margin-left:0;}
  }

  @media (max-width: 560px){
    .msg-shell{border-radius:24px;}
    .msg-header{padding:20px 18px 14px;}
    .msg-tabs{padding:8px 18px 0;}
    .msg-quick-view{grid-template-columns:1fr;justify-items:center;}
    .msg-channel-strip{display:grid;grid-template-columns:1fr;}
    .msg-activity-grid{grid-template-columns:1fr;}
  }

  /* LOOP COMMUNICATION CENTER REVAMP — web + mobile polish */
  .msg-comm-center-shell{
    border-radius:28px!important;
    overflow:hidden!important;
    background:rgba(255,255,255,.94)!important;
    border:1px solid rgba(226,232,240,.92)!important;
    box-shadow:0 18px 55px rgba(15,23,42,.06)!important;
  }
  .msg-comm-overview{
    display:grid;
    grid-template-columns:minmax(0,1fr) minmax(420px,.9fr);
    gap:18px;
    align-items:stretch;
    padding:22px 24px 18px;
    background:linear-gradient(135deg,rgba(251,247,239,.96),rgba(255,255,255,.96) 52%,rgba(232,247,242,.68));
    border-bottom:1px solid rgba(226,232,240,.78);
  }
  .msg-comm-overview-main{display:grid;align-content:center;min-width:0;}
  .msg-comm-kicker{
    display:inline-flex;
    width:max-content;
    align-items:center;
    height:24px;
    padding:0 10px;
    border-radius:999px;
    background:rgba(0,103,95,.09);
    color:#00675f;
    font-size:11px;
    font-weight:950;
    letter-spacing:.12em;
    text-transform:uppercase;
  }
  .msg-comm-overview h2{margin:10px 0 0;color:#0f172a;font-size:clamp(24px,2vw,34px);font-weight:950;letter-spacing:-.06em;line-height:1;}
  .msg-comm-overview p{margin:8px 0 0;max-width:560px;color:#64748b;font-size:13px;font-weight:760;line-height:1.45;}
  .msg-comm-health-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;align-items:stretch;}
  .msg-comm-health-card{
    border:1px solid rgba(226,232,240,.9);
    background:rgba(255,255,255,.9);
    border-radius:18px;
    padding:14px 12px;
    min-height:112px;
    display:grid;
    justify-items:start;
    align-content:center;
    gap:7px;
    text-align:left;
    cursor:pointer;
    color:#0f172a;
    box-shadow:0 10px 28px rgba(15,23,42,.045);
  }
  .msg-comm-health-card:hover{border-color:rgba(0,103,95,.24);transform:translateY(-1px);}
  .msg-comm-health-card .msg-icon{width:34px;height:34px;}
  .msg-comm-health-card span{font-size:11px;font-weight:900;color:#64748b;text-transform:uppercase;letter-spacing:.08em;}
  .msg-comm-health-card strong{font-size:17px;font-weight:950;color:#0f172a;line-height:1;}
  .msg-comm-health-card.is-on strong{color:#00675f;}
  .msg-comm-health-card.is-off strong{color:#94a3b8;}
  .msg-comm-tabs{
    position:sticky;
    top:0;
    z-index:10;
    background:rgba(255,255,255,.92)!important;
    backdrop-filter:blur(12px);
    -webkit-backdrop-filter:blur(12px);
    border-bottom:1px solid rgba(226,232,240,.76)!important;
    padding:10px 18px!important;
    gap:8px!important;
    overflow:auto!important;
    scrollbar-width:none;
  }
  .msg-comm-tabs::-webkit-scrollbar{display:none;}
  .msg-comm-tabs button{
    flex:0 0 auto;
    height:38px!important;
    border-radius:999px!important;
    border:1px solid rgba(226,232,240,.95)!important;
    background:#fff!important;
    color:#475569!important;
    padding:0 15px!important;
    font-size:13px!important;
    font-weight:950!important;
    box-shadow:none!important;
  }
  .msg-comm-tabs button.active{
    border-color:rgba(0,103,95,.22)!important;
    background:rgba(0,103,95,.09)!important;
    color:#00675f!important;
  }
  .msg-tab-content{padding:20px!important;background:linear-gradient(180deg,#fff,#fbfaf7)!important;}
  .msg-automation-hero{
    border-radius:24px!important;
    padding:18px!important;
    background:linear-gradient(135deg,#ffffff,rgba(232,247,242,.72))!important;
    border:1px solid rgba(226,232,240,.82)!important;
    box-shadow:0 12px 34px rgba(15,23,42,.045)!important;
  }
  .msg-channel-strip{background:rgba(255,255,255,.84)!important;border:1px solid rgba(226,232,240,.74)!important;border-radius:18px!important;padding:10px!important;}
  .msg-content-grid{grid-template-columns:minmax(0,1.55fr) minmax(280px,.72fr)!important;gap:18px!important;}
  .msg-main-card,.msg-side-card,.msg-channel-card,.msg-security-card,.msg-upgrade-card,.msg-activity-table-card,.msg-distribution{
    border-radius:24px!important;
    border:1px solid rgba(226,232,240,.88)!important;
    box-shadow:0 14px 40px rgba(15,23,42,.045)!important;
    background:rgba(255,255,255,.94)!important;
  }
  .msg-card-title{margin-bottom:14px!important;}
  .msg-card-title h3,.msg-section-intro h3,.msg-channel-card h3,.msg-side-card h3{font-size:18px!important;letter-spacing:-.035em!important;}
  .msg-card-title p,.msg-section-intro p,.msg-channel-card p,.msg-side-card p{font-size:12.5px!important;line-height:1.4!important;}
  .msg-moments-list{gap:10px!important;}
  .msg-moment-row{
    min-height:78px!important;
    border-radius:18px!important;
    padding:13px 14px!important;
    grid-template-columns:42px minmax(0,1fr) auto 58px!important;
    gap:12px!important;
    border:1px solid rgba(226,232,240,.76)!important;
    background:#fff!important;
  }
  .msg-moment-row>.msg-icon{width:38px;height:38px;}
  .msg-moment-copy strong{font-size:14px!important;}
  .msg-moment-copy span{font-size:12px!important;line-height:1.35!important;}
  .msg-channel-button{width:34px!important;height:34px!important;border-radius:12px!important;}
  .msg-toggle{width:48px!important;height:28px!important;}
  .msg-toggle span{width:22px!important;height:22px!important;}
  .msg-toggle.is-on span{transform:translateX(20px)!important;}
  .msg-add-button{display:none!important;}
  .msg-side-stack{gap:12px!important;}
  .msg-upgrade-card,.msg-security-card{padding:18px!important;}
  .msg-channel-cards{grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:14px!important;}
  .msg-channel-card{padding:18px!important;}
  .msg-channel-card-head{display:grid!important;grid-template-columns:38px minmax(0,1fr) auto!important;gap:12px!important;align-items:start!important;}
  .msg-channel-card-head .msg-icon{width:38px;height:38px;}
  .msg-channel-card-head button{height:32px!important;border-radius:10px!important;font-size:12px!important;margin-top:8px!important;}
  .msg-channel-config{margin-top:14px!important;border-radius:16px!important;padding:12px!important;background:#f8fafc!important;}
  .msg-stats-link{margin-top:12px!important;height:36px!important;border-radius:12px!important;}
  .msg-activity-grid{grid-template-columns:repeat(5,minmax(0,1fr))!important;gap:12px!important;}
  .msg-metric{border-radius:18px!important;padding:16px!important;min-height:100px!important;}
  .msg-metric strong{font-size:24px!important;}
  .msg-activity-layout{grid-template-columns:minmax(0,1fr) 280px!important;gap:16px!important;}
  .msg-activity-toolbar{gap:12px!important;}

  @media (max-width:1180px){
    .msg-comm-overview{grid-template-columns:1fr;}
    .msg-comm-health-grid{grid-template-columns:repeat(4,minmax(0,1fr));}
    .msg-content-grid,.msg-activity-layout{grid-template-columns:1fr!important;}
    .msg-channel-cards{grid-template-columns:1fr!important;}
    .msg-activity-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important;}
  }
  @media (max-width:720px){
    .notify-settings-page.msg-page{gap:10px!important;}
    .msg-comm-center-shell{border-radius:22px!important;margin:0!important;box-shadow:0 10px 28px rgba(15,23,42,.045)!important;}
    .msg-comm-overview{padding:16px!important;gap:12px!important;}
    .msg-comm-kicker{height:21px;font-size:10px;padding:0 8px;}
    .msg-comm-overview h2{font-size:22px!important;margin-top:8px!important;}
    .msg-comm-overview p{font-size:12px!important;margin-top:5px!important;}
    .msg-comm-health-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
    .msg-comm-health-card{min-height:76px!important;border-radius:16px!important;padding:10px!important;gap:4px!important;}
    .msg-comm-health-card .msg-icon{width:28px!important;height:28px!important;}
    .msg-comm-health-card span{font-size:9.5px!important;}
    .msg-comm-health-card strong{font-size:14px!important;}
    .msg-comm-tabs{padding:9px 12px!important;top:0;}
    .msg-comm-tabs button{height:34px!important;padding:0 13px!important;font-size:12px!important;}
    .msg-tab-content{padding:12px!important;}
    .msg-automation-hero{display:none!important;}
    .msg-content-grid{gap:12px!important;}
    .msg-main-card{padding:14px!important;border-radius:20px!important;}
    .msg-card-title{margin-bottom:10px!important;}
    .msg-card-title h3{font-size:16px!important;}
    .msg-card-title p{font-size:12px!important;}
    .msg-moment-row{
      grid-template-columns:34px minmax(0,1fr) 48px!important;
      min-height:auto!important;
      padding:12px!important;
      border-radius:16px!important;
      gap:9px!important;
    }
    .msg-moment-row>.msg-icon{width:32px!important;height:32px!important;}
    .msg-moment-copy strong{font-size:13px!important;}
    .msg-moment-copy span{font-size:11.5px!important;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
    .msg-moment-channels{grid-column:1/-1!important;display:flex!important;gap:8px!important;justify-content:flex-start!important;padding-left:0!important;}
    .msg-channel-button{width:32px!important;height:32px!important;border-radius:11px!important;}
    .msg-toggle{width:46px!important;height:26px!important;justify-self:end!important;}
    .msg-toggle span{width:20px!important;height:20px!important;}
    .msg-toggle.is-on span{transform:translateX(20px)!important;}
    .msg-side-stack,.msg-upgrade-card,.msg-security-card{display:none!important;}
    .msg-section-intro{padding:2px 2px 0!important;margin-bottom:10px!important;}
    .msg-section-intro h3{font-size:17px!important;}
    .msg-section-intro p{font-size:12px!important;}
    .msg-channel-cards{gap:10px!important;}
    .msg-channel-card{padding:14px!important;border-radius:18px!important;}
    .msg-channel-card-head{grid-template-columns:34px minmax(0,1fr) auto!important;}
    .msg-channel-card-head .msg-icon{width:32px!important;height:32px!important;}
    .msg-channel-card h3{font-size:15px!important;}
    .msg-channel-card p{font-size:11.5px!important;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
    .msg-channel-config{padding:10px!important;margin-top:10px!important;}
    .msg-channel-config div{grid-template-columns:1fr auto!important;font-size:11.5px!important;}
    .msg-activity-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:8px!important;}
    .msg-metric{min-height:76px!important;border-radius:16px!important;padding:11px!important;}
    .msg-metric strong{font-size:20px!important;}
    .msg-metric span{font-size:11px!important;}
    .msg-activity-toolbar{display:grid!important;gap:10px!important;}
    .msg-activity-toolbar>div{display:grid!important;grid-template-columns:1fr 1fr!important;gap:8px!important;}
    .msg-activity-toolbar>div span{grid-column:1/-1!important;}
    .msg-activity-table{overflow:visible!important;display:grid!important;gap:8px!important;}
    .msg-activity-table-head{display:none!important;}
    .msg-activity-row{
      min-width:0!important;
      display:grid!important;
      grid-template-columns:1fr auto!important;
      gap:8px!important;
      border:1px solid rgba(226,232,240,.78)!important;
      border-radius:16px!important;
      padding:12px!important;
      background:#fff!important;
    }
    .msg-activity-row>span:nth-of-type(1),.msg-activity-row>span:nth-of-type(2){display:none!important;}
    .msg-activity-row>span:last-child{grid-column:1/-1;color:#94a3b8;font-size:11px!important;}
    .msg-distribution{display:none!important;}
  }


  /* LOOP NOTIFICATION COMPACT BUBBLES FIX — less oversized cards/bubbles on web + mobile */
  .msg-comm-center-shell{
    border-radius:22px!important;
    box-shadow:0 10px 28px rgba(15,23,42,.038)!important;
  }
  .msg-comm-overview{
    padding:18px!important;
    gap:16px!important;
    align-items:center!important;
    border-radius:0!important;
  }
  .msg-comm-overview h2{
    font-size:clamp(22px,1.9vw,28px)!important;
    letter-spacing:-.045em!important;
  }
  .msg-comm-overview p{
    max-width:540px!important;
    font-size:12.5px!important;
    line-height:1.35!important;
  }
  .msg-comm-kicker{
    height:24px!important;
    padding:0 9px!important;
    font-size:10px!important;
  }
  .msg-comm-health-grid{
    gap:10px!important;
    grid-template-columns:repeat(4,minmax(0,1fr))!important;
  }
  .msg-comm-health-card{
    min-height:78px!important;
    border-radius:15px!important;
    padding:10px 11px!important;
    gap:4px!important;
    box-shadow:none!important;
  }
  .msg-comm-health-card:hover{transform:none!important;}
  .msg-comm-health-card .msg-icon{width:27px!important;height:27px!important;}
  .msg-comm-health-card span{font-size:9.5px!important;letter-spacing:.07em!important;}
  .msg-comm-health-card strong{font-size:14px!important;}
  .msg-comm-tabs{
    padding:8px 14px!important;
    gap:6px!important;
  }
  .msg-comm-tabs button{
    height:32px!important;
    padding:0 12px!important;
    border-radius:11px!important;
    font-size:12px!important;
  }
  .msg-tab-content{padding:16px!important;}
  .msg-automation-hero{
    border-radius:18px!important;
    padding:14px!important;
    box-shadow:none!important;
  }
  .msg-hero-copy{gap:12px!important;}
  .msg-hero-copy>.msg-icon{width:40px!important;height:40px!important;}
  .msg-hero-copy h3{font-size:16px!important;}
  .msg-hero-copy p{font-size:12px!important;}
  .msg-channel-strip{gap:8px!important;padding:8px!important;border-radius:14px!important;}
  .msg-channel-strip>div{min-width:104px!important;grid-template-columns:28px minmax(0,1fr)!important;padding-left:8px!important;column-gap:7px!important;}
  .msg-channel-strip .msg-icon{width:28px!important;height:28px!important;}
  .msg-channel-strip strong{font-size:12px!important;}
  .msg-channel-strip span{font-size:10.5px!important;}
  .msg-content-grid{gap:14px!important;grid-template-columns:minmax(0,1.7fr) minmax(250px,.58fr)!important;}
  .msg-main-card,.msg-side-card,.msg-channel-card,.msg-security-card,.msg-upgrade-card,.msg-activity-table-card,.msg-distribution{
    border-radius:18px!important;
    box-shadow:none!important;
  }
  .msg-main-card{padding:12px!important;}
  .msg-card-title{padding:4px 4px 10px!important;margin-bottom:6px!important;}
  .msg-card-title h3,.msg-section-intro h3,.msg-channel-card h3,.msg-side-card h3{font-size:16px!important;}
  .msg-card-title p,.msg-section-intro p,.msg-channel-card p,.msg-side-card p{font-size:12px!important;}
  .msg-moments-list{gap:8px!important;}
  .msg-moment-row{
    min-height:64px!important;
    border-radius:14px!important;
    padding:10px 11px!important;
    grid-template-columns:34px minmax(0,1fr) auto 48px!important;
    gap:9px!important;
  }
  .msg-moment-row>.msg-icon{width:32px!important;height:32px!important;}
  .msg-moment-copy strong{font-size:13px!important;}
  .msg-moment-copy span{font-size:11.2px!important;margin-top:2px!important;}
  .msg-channel-button{width:28px!important;height:28px!important;border-radius:9px!important;}
  .msg-channel-button .msg-icon{width:25px!important;height:25px!important;}
  .msg-toggle{width:42px!important;height:24px!important;padding:3px!important;}
  .msg-toggle span{width:18px!important;height:18px!important;}
  .msg-toggle.is-on span{transform:translateX(18px)!important;}
  .msg-side-stack{gap:10px!important;}
  .msg-side-card,.msg-upgrade-card,.msg-security-card{padding:14px!important;}
  .msg-quick-view{grid-template-columns:104px minmax(0,1fr)!important;gap:10px!important;}
  .msg-donut{width:96px!important;height:96px!important;}
  .msg-donut:after{inset:12px!important;}
  .msg-donut strong{font-size:18px!important;}
  .msg-donut span{font-size:10.5px!important;max-width:60px!important;}
  .msg-donut.large{width:108px!important;height:108px!important;}
  .msg-donut.large:after{inset:14px!important;}
  .msg-legend{gap:8px!important;}
  .msg-legend span{font-size:11.5px!important;gap:7px!important;}
  .msg-channel-cards{gap:10px!important;}
  .msg-channel-card{padding:14px!important;}
  .msg-channel-card-head{grid-template-columns:32px minmax(0,1fr) auto!important;gap:9px!important;}
  .msg-channel-card-head .msg-icon{width:31px!important;height:31px!important;}
  .msg-channel-card-head button{height:29px!important;border-radius:9px!important;font-size:11px!important;padding:0 9px!important;}
  .msg-channel-config{border-radius:13px!important;padding:9px!important;margin-top:9px!important;}
  .msg-stats-link{height:32px!important;border-radius:10px!important;font-size:12px!important;}
  .msg-activity-grid{gap:8px!important;grid-template-columns:repeat(5,minmax(0,1fr))!important;}
  .msg-metric{min-height:76px!important;border-radius:14px!important;padding:11px!important;box-shadow:none!important;}
  .msg-metric strong{font-size:20px!important;}
  .msg-metric span{font-size:10.8px!important;}
  .msg-activity-layout{gap:12px!important;grid-template-columns:minmax(0,1fr) 240px!important;}

  @media (max-width:1180px){
    .msg-comm-health-grid{grid-template-columns:repeat(4,minmax(0,1fr))!important;}
    .msg-content-grid,.msg-activity-layout{grid-template-columns:1fr!important;}
    .msg-channel-cards{grid-template-columns:1fr!important;}
    .msg-activity-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important;}
  }

  @media (max-width:720px){
    .msg-comm-center-shell{border-radius:18px!important;box-shadow:0 6px 18px rgba(15,23,42,.035)!important;}
    .msg-comm-overview{padding:12px!important;gap:9px!important;}
    .msg-comm-overview h2{font-size:19px!important;margin-top:5px!important;}
    .msg-comm-overview p{display:none!important;}
    .msg-comm-kicker{height:19px!important;font-size:9px!important;padding:0 7px!important;}
    .msg-comm-health-grid{grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:6px!important;}
    .msg-comm-health-card{
      min-height:58px!important;
      border-radius:12px!important;
      padding:7px 6px!important;
      align-content:center!important;
      justify-items:center!important;
      text-align:center!important;
    }
    .msg-comm-health-card .msg-icon{width:22px!important;height:22px!important;}
    .msg-comm-health-card span{display:none!important;}
    .msg-comm-health-card strong{font-size:11.5px!important;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
    .msg-comm-tabs{padding:7px 10px!important;}
    .msg-comm-tabs button{height:30px!important;border-radius:10px!important;font-size:11.5px!important;padding:0 10px!important;}
    .msg-tab-content{padding:10px!important;}
    .msg-main-card,.msg-channel-card,.msg-activity-table-card{border-radius:16px!important;padding:10px!important;}
    .msg-card-title{padding:0 1px 8px!important;}
    .msg-card-title h3,.msg-section-intro h3{font-size:15px!important;}
    .msg-card-title p,.msg-section-intro p{font-size:11.5px!important;}
    .msg-moments-list{gap:7px!important;}
    .msg-moment-row{
      grid-template-columns:28px minmax(0,1fr) 42px!important;
      border-radius:13px!important;
      padding:9px!important;
      gap:7px!important;
    }
    .msg-moment-row>.msg-icon{width:28px!important;height:28px!important;}
    .msg-moment-copy strong{font-size:12.5px!important;}
    .msg-moment-copy span{font-size:10.8px!important;-webkit-line-clamp:1!important;}
    .msg-moment-channels{gap:6px!important;}
    .msg-channel-button{width:27px!important;height:27px!important;}
    .msg-toggle{width:40px!important;height:23px!important;}
    .msg-toggle span{width:17px!important;height:17px!important;}
    .msg-toggle.is-on span{transform:translateX(17px)!important;}
    .msg-channel-card{padding:11px!important;}
    .msg-channel-card-head{grid-template-columns:28px minmax(0,1fr) auto!important;gap:8px!important;}
    .msg-channel-card-head .msg-icon{width:28px!important;height:28px!important;}
    .msg-channel-card h3{font-size:14px!important;}
    .msg-channel-card p{font-size:11px!important;-webkit-line-clamp:1!important;}
    .msg-channel-config{padding:8px!important;border-radius:12px!important;}
    .msg-activity-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
    .msg-metric{min-height:62px!important;border-radius:12px!important;padding:9px!important;}
    .msg-metric strong{font-size:17px!important;}
    .msg-metric span{font-size:10px!important;}
  }

`;
